import { useState, useEffect, useRef, useCallback } from 'react'
import { analytics } from './lib/firebaseClient'
import { logEvent } from 'firebase/analytics'
import { buildApiUrl } from './lib/config'

export const LOGO_SRC = '/images/fretpractice-logo.png'

// ═══ TWITTER CONFIG ════════════════════════════════════════════════
// 1. Deploy api/tweets.ts to your Vercel project
// 2. Add TWITTER_BEARER_TOKEN to Vercel environment variables
// 3. Set LIVE_MODE: true below — that is all you need!
// ═══════════════════════════════════════════════════════════════════
const TWITTER_CONFIG = {
  LIVE_MODE: true,
  FRETPRACTICE_HANDLE: 'fretpracticenow',
  FOUNDER_HANDLE: 'rivuchakraborty',
}

// ─── TYPES ───────────────────────────────────────────────────────────
interface Feature { num: string; frontTitle: string; frontTagline: string; backTitle: string; backDesc: string; icon: string }
interface PricingFeature { name: string; free: string; premium: string; ultra: string }
interface PricingTierData { tier: string; priceINR: string; priceUSD: string; note: string; badge?: string; hot?: boolean; cta: string }
interface StatItem { val: string; lbl: string }
interface StepItem { n: string; title: string; desc: string }
interface SocialItem { label: string; path: string; url?: string }
interface Tweet { id: string; text: string; date: string; likes: number; retweets: number; author: string; handle: string; avatar: string }
interface ModalState { name: string; email: string; whatsapp: string; countryCode: string; experience: string; featureRequest: string; willingToResearch: boolean; done: boolean; error: boolean }
interface TeamMemberItem { id: number; name: string; role: string; image: string; twitter?: string; linkedin?: string }

// ─── TEAM DATA ───────────────────────────────────────────────────────
const TEAM_MEMBERS: TeamMemberItem[] = [
  { id: 1, name: 'Rivu Chakraborty', role: 'Founder & CTO', image: 'images/rivu.jpeg', twitter: 'rivuchakraborty', linkedin: 'rivuchk' },
  { id: 2, name: 'Arpan Sinha', role: "Founder's Office", image: '/images/Mr. Sinha.png', twitter: 'mr_sinhaaaa', linkedin: 'arpansinhaaaa' },
  { id: 3, name: 'Srijan Mukherjee', role: 'ML Intern & Lead Guitarist', image: '/images/srijan.png', twitter: 'technosrijan', linkedin: 'technosrijan' },
  { id: 4, name: 'Srishti Lakhera', role: 'Design Lead', image: '/images/srishti.png', twitter: '', linkedin: 'srishti-lakhera' },
  { id: 5, name: 'Ishika Sen', role: 'UX/UI Design Intern', image: '/images/ishika.png', twitter: '', linkedin: 'ishika-sen-755b69221' },
  { id: 6, name: 'Mussarrat Kittur', role: 'Content Intern', image: '/images/mussarat.png', twitter: 'mussarratkittur', linkedin: 'mussarrat-kittur-8b6aa3295' },
  { id: 7, name: 'Tuhin Bhowmick', role: 'Associate KMP Developer', image: '/images/tuhin.png', twitter: 'tuuhin_', linkedin: 'tuuhin' },
  { id: 8, name: 'Ritabrata Mondal', role: 'Video Editor', image: '/images/rito.png', twitter: '', linkedin: 'ritabrata-mondal-94621a311' },
]
const TEAM_ADVISORS: TeamMemberItem[] = [
  { id: 101, name: 'Narendra Nath Chatterjee', role: 'Advisor', image: '/images/narendra.png', twitter: 'Narendr83598942', linkedin: 'narendra-nath-chatterjee-8a7651133/' },
  { id: 102, name: 'Imran Roshan', role: 'Advisor', image: '/images/imran.png', twitter: 'imranfosec', linkedin: 'imranfosec' },
  { id: 103, name: 'Nikhilesh Tayal', role: 'Advisor', image: '/images/Nikhilesh.png', twitter: 'losesaga', linkedin: 'nikhileshtayal' },
]

// ─── MOCK TWEETS ────────────────────────────────────────────────────
const MOCK_TWEETS: Tweet[] = [
  {
    id: '1', author: 'FretPractice', handle: '@FretPracticeNow', avatar: '/images/fretpractice_logo.svg',
    text: 'Structured practice > random noodling. Every session in FretPractice has a goal, a feedback loop, and a measurable outcome. Building the system guitarists actually need.',
    date: 'Mar 18', likes: 24, retweets: 8
  },
  {
    id: '2', author: 'Rivu Chakraborty', handle: '@rivuchakraborty', avatar: '/images/rivu.jpeg',
    text: "Been playing guitar for 18 years. Still remember the frustration of not knowing if I was improving. That feeling is exactly why I\'m building @FretPractice.",
    date: 'Mar 15', likes: 61, retweets: 12
  },
  {
    id: '3', author: 'FretPractice', handle: '@FretPracticeNow', avatar: '/images/fretpractice_logo.svg',
    text: "Real-time audio feedback is live in internal testing. You play a note — FretPractice tells you if it\'s right instantly. No more guessing. No more bad habits going unnoticed.",
    date: 'Mar 12', likes: 43, retweets: 9
  },
  {
    id: '4', author: 'Rivu Chakraborty', handle: '@rivuchakraborty', avatar: '/images/rivu.jpeg',
    text: 'Why do most guitar apps fail? They give you tools but no structure. A metronome is useless without a practice plan. @FretPractice fixes this.',
    date: 'Mar 9', likes: 89, retweets: 21
  },
  {
    id: '5', author: 'FretPractice', handle: '@FretPracticeNow', avatar: '/images/fretpractice_logo.svg',
    text: 'Dhun — our AI music engine — can now generate backing tracks in any key and tempo. Practice with real music context, not just a click track. Coming soon to early access.',
    date: 'Mar 6', likes: 55, retweets: 14
  },
  {
    id: '6', author: 'Rivu Chakraborty', handle: '@rivuchakraborty', avatar: '/images/rivu.jpeg',
    text: "Progress tracking is the most underrated feature in any practice tool. If you can\'t measure it, you can\'t improve it. @FretPractice shows you exactly where you\'ve grown.",
    date: 'Mar 3', likes: 37, retweets: 7
  },
]

// ─── HOOKS ───────────────────────────────────────────────────────────
function useWindowWidth(): number {
  const [w, setW] = useState(0)
  useEffect(() => { const fn = () => setW(window.innerWidth); fn(); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn) }, [])
  return w
}
function useScrollY(): number {
  const [y, setY] = useState(0)
  useEffect(() => { const fn = () => setY(window.scrollY); window.addEventListener('scroll', fn, { passive: true }); return () => window.removeEventListener('scroll', fn) }, [])
  return y
}
function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null)
  const [inView, setInView] = useState(false)
  useEffect(() => { const el = ref.current; if (!el) return; const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect() } }, { threshold, rootMargin: '0px 0px -60px 0px' }); io.observe(el); return () => io.disconnect() }, [threshold])
  return { ref, inView }
}
function useTimer(start = 23 * 60 + 45): string {
  const [s, setS] = useState(start)
  useEffect(() => { const t = setInterval(() => setS(x => x + 1), 1000); return () => clearInterval(t) }, [])
  return `${String(Math.floor(s / 60) % 60).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}
function useTweets() {
  const [tweets, setTweets] = useState<Tweet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [lastFetched, setLastFetched] = useState<Date | null>(null)
  const fetch_ = useCallback(async () => {
    if (!TWITTER_CONFIG.LIVE_MODE) {
      await new Promise(r => setTimeout(r, 700))
      setTweets(MOCK_TWEETS); setLoading(false); setLastFetched(new Date()); return
    }
    try {
      setError(false)
      setLoading(true)

      // Fetch tweets from API server
      const usernames = `${TWITTER_CONFIG.FRETPRACTICE_HANDLE},${TWITTER_CONFIG.FOUNDER_HANDLE}`
      const apiUrl = buildApiUrl(`/api/tweets?usernames=${usernames}&maxResults=50`)
      const response = await fetch(apiUrl)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const liveTweets = await response.json()
      setTweets(liveTweets.length > 0 ? liveTweets : MOCK_TWEETS)
      setLastFetched(new Date())
    } catch (error) {
      console.warn('Twitter API unavailable, using mock tweets:', error instanceof Error ? error.message : 'Unknown error')
      setError(false) // Don't show as error since fallback is working
      setTweets(MOCK_TWEETS)
    }
    finally { setLoading(false) }
  }, [])
  useEffect(() => { fetch_(); const t = setInterval(fetch_, 5 * 60 * 1000); return () => clearInterval(t) }, [fetch_])
  return { tweets, loading, error, lastFetched, refresh: fetch_ }
}

// ─── GLOBAL STYLES ──────────────────────────────────────────────────
function GlobalStyles() {
  useEffect(() => {
    const s = document.createElement('style')
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');
      *,*::before,*::after{box-sizing:border-box}
      html{scroll-behavior:smooth}
      body{margin:0;padding:0;background:#000;color:rgba(255,255,255,0.88);font-family:'DM Sans',sans-serif;font-weight:300;overflow-x:hidden;-webkit-font-smoothing:antialiased}
      body::after{content:'';position:fixed;inset:0;pointer-events:none;z-index:9998;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23g)' opacity='0.05'/%3E%3C/svg%3E");opacity:0.28}
      ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:#000}::-webkit-scrollbar-thumb{background:rgba(37,89,244,0.35);border-radius:10px}
      @keyframes riseIn{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
      @keyframes breathePulse{0%,100%{box-shadow:0 0 0 4px rgba(37,89,244,0.2)}50%{box-shadow:0 0 0 8px rgba(37,89,244,0.06)}}
      @keyframes floatA{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(35px,-25px) scale(1.04)}66%{transform:translate(-20px,30px) scale(0.97)}}
      @keyframes floatB{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(-28px,18px) scale(1.03)}66%{transform:translate(22px,-32px) scale(0.98)}}
      @keyframes scFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
      @keyframes fbPulse{0%,100%{opacity:1}50%{opacity:0.55}}
      @keyframes wvPump{0%,100%{transform:scaleY(1)}50%{transform:scaleY(0.25)}}
      @keyframes marqueeScroll{from{transform:translateX(0)}to{transform:translateX(-50%)}}
      @keyframes fadeIn{from{opacity:0}to{opacity:1}}
      @keyframes slideUp{from{transform:translateY(20px) scale(.96);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}
      @keyframes skeletonPulse{0%,100%{opacity:0.35}50%{opacity:0.65}}
    `
    document.head.appendChild(s)
    return () => { document.head.removeChild(s) }
  }, [])
  return null
}

// ─── CURSOR ──────────────────────────────────────────────────────────

// ─── HELPERS ─────────────────────────────────────────────────────────
function scrollTo(id: string) {
  const el = document.getElementById(id)
  if (el) {
    window.scrollTo({ top: el.offsetTop - 76, behavior: 'smooth' })
    try {
      window.history.pushState(null, '', `/#${id}`)
    } catch (e) { }
  } else {
    window.location.href = `/#${id}`
  }
}
function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const { ref, inView } = useInView()
  return <div ref={ref} style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: `opacity .85s cubic-bezier(.16,1,.3,1) ${delay}s,transform .85s cubic-bezier(.16,1,.3,1) ${delay}s` }}>{children}</div>
}
function Eyebrow({ children, center = false }: { children: React.ReactNode; center?: boolean }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: center ? 'center' : 'flex-start', gap: 12, fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#2559F4', marginBottom: 22 }}>
      <span style={{ display: 'block', width: 24, height: 1, background: '#2559F4', flexShrink: 0 }} />
      {children}
      {center && <span style={{ display: 'block', width: 24, height: 1, background: '#2559F4', flexShrink: 0 }} />}
    </div>
  )
}
function HRule() { return <div style={{ height: 1, background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.07),transparent)' }} /> }

// ─── BUTTONS ─────────────────────────────────────────────────────────
function BtnBlue({ children, onClick, fullWidth = false, style = {}, disabled = false }: { children: React.ReactNode; onClick?: () => void; fullWidth?: boolean; style?: React.CSSProperties; disabled?: boolean }) {
  const [hov, setHov] = useState(false)
  return (
    <button onClick={onClick} disabled={disabled} data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: disabled ? 'rgba(37,89,244,0.5)' : '#2559F4', color: disabled ? 'rgba(255,255,255,0.7)' : '#fff', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500, padding: '14px 30px', borderRadius: 10, width: fullWidth ? '100%' : 'auto', boxShadow: hov && !disabled ? '0 0 0 1px rgba(37,89,244,0.7),0 16px 48px rgba(37,89,244,0.48)' : 'none', transform: hov && !disabled ? 'translateY(-2px)' : 'none', transition: 'box-shadow .35s cubic-bezier(.16,1,.3,1),transform .3s cubic-bezier(.16,1,.3,1)', ...style }}>
      {children}
    </button>
  )
}
function BtnOutline({ children, onClick, fullWidth = false }: { children: React.ReactNode; onClick?: () => void; fullWidth?: boolean }) {
  const [hov, setHov] = useState(false)
  return (
    <button onClick={onClick} data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: hov ? 'rgba(255,255,255,0.04)' : 'transparent', color: hov ? '#fff' : 'rgba(255,255,255,0.52)', border: '1px solid rgba(255,255,255,0.14)', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 400, padding: '14px 26px', borderRadius: 10, width: fullWidth ? '100%' : 'auto', transform: hov ? 'translateY(-1px)' : 'none', transition: 'color .25s,background .25s,transform .3s cubic-bezier(.16,1,.3,1)' }}>
      {children}
    </button>
  )
}

// ─── DOWNLOAD BUTTONS ───────────────────────────────────────────────
function DownloadButtons({ size = 'md', direction = 'row' }: { size?: 'sm' | 'md'; direction?: 'row' | 'column' }) {
  const [hovPlay, setHovPlay] = useState(false)
  const [hovApple, setHovApple] = useState(false)

  const h = size === 'sm' ? 38 : 46
  const fontSize = size === 'sm' ? 10 : 11
  const titleSize = size === 'sm' ? 13 : 14

  const btnBase: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 10,
    background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: size === 'sm' ? '8px 14px' : '10px 18px',
    textDecoration: 'none', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.14)',
    transition: 'transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s, background .3s',
    height: h,
  }

  return (
    <div style={{ display: 'flex', flexDirection: direction, gap: size === 'sm' ? 8 : 10, flexWrap: 'wrap', justifyContent: 'center' }}>
      {/* Google Play — colorful icon */}
      <a
        href="https://play.google.com/store/apps/details?id=com.fretpractice.app"
        target="_blank"
        rel="noopener noreferrer"
        data-hover
        onMouseEnter={() => setHovPlay(true)}
        onMouseLeave={() => setHovPlay(false)}
        style={{ ...btnBase, background: hovPlay ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)', transform: hovPlay ? 'translateY(-2px)' : 'none', boxShadow: hovPlay ? '0 8px 28px rgba(0,0,0,0.35)' : 'none' }}
      >
        {/* Official Google Play 4-color icon */}
        <svg viewBox="0 0 24 24" style={{ width: size === 'sm' ? 18 : 22, height: size === 'sm' ? 18 : 22, flexShrink: 0 }}>
          <path d="M3.18 23.75c.34.19.72.25 1.1.19L14.84 12 11.4 8.56 3.18 23.75z" fill="#EA4335" />
          <path d="M20.64 10.35l-2.94-1.67-3.68 3.32 3.68 3.67 2.97-1.69c.85-.48.85-1.63-.03-2.63z" fill="#FBBC04" />
          <path d="M4.28.25a1.47 1.47 0 0 0-.5.44l10.62 10.63 3.44-3.44L4.28.25z" fill="#34A853" />
          <path d="M3.68.69a1.47 1.47 0 0 0-.5 1.16v20.3c0 .45.18.86.5 1.16l.1.09L14.84 12v-.27L3.78.6l-.1.09z" fill="#4285F4" />
        </svg>
        <div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize, color: 'rgba(255,255,255,0.45)', lineHeight: 1, marginBottom: 2 }}>Get it on</div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: titleSize, fontWeight: 600, color: '#fff', lineHeight: 1 }}>Google Play</div>
        </div>
      </a>

      {/* App Store — Coming Soon */}
      <div
        data-hover
        onMouseEnter={() => setHovApple(true)}
        onMouseLeave={() => setHovApple(false)}
        style={{ ...btnBase, position: 'relative', opacity: 0.5, cursor: 'not-allowed' }}
      >
        <svg viewBox="0 0 24 24" style={{ width: size === 'sm' ? 18 : 22, height: size === 'sm' ? 18 : 22, flexShrink: 0, fill: 'rgba(255,255,255,0.9)' }}>
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
        </svg>
        <div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize, color: 'rgba(255,255,255,0.45)', lineHeight: 1, marginBottom: 2 }}>Download on the</div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: titleSize, fontWeight: 600, color: '#fff', lineHeight: 1 }}>App Store</div>
        </div>
        {/* Coming Soon badge — below the button, CSS-animated, never overlaps content above */}
        <div style={{
          position: 'absolute',
          bottom: size === 'sm' ? -26 : -30,
          left: '50%',
          background: 'rgba(37,89,244,0.92)',
          backdropFilter: 'blur(10px)',
          color: '#fff',
          fontFamily: "'DM Sans',sans-serif",
          fontSize: 9,
          fontWeight: 600,
          padding: '3px 10px',
          borderRadius: 100,
          whiteSpace: 'nowrap',
          letterSpacing: '0.07em',
          textTransform: 'uppercase' as const,
          border: '1px solid rgba(255,255,255,0.22)',
          pointerEvents: 'none',
          zIndex: 10,
          opacity: hovApple ? 1 : 0,
          transform: hovApple ? 'translateX(-50%) translateY(0px)' : 'translateX(-50%) translateY(-5px)',
          transition: 'opacity .2s ease, transform .25s cubic-bezier(.16,1,.3,1)',
        }}>
          Coming Soon
        </div>
      </div>
    </div>
  )
}

// ─── NAV ─────────────────────────────────────────────────────────────
function NavBtn({ label, id, onClick }: { label: string; id: string; onClick?: () => void }) {
  const [hov, setHov] = useState(false)
  return (
    <button onClick={onClick || (() => scrollTo(id))} data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? 'rgba(255,255,255,0.05)' : 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 400, color: hov ? '#fff' : 'rgba(255,255,255,0.5)', padding: '6px 14px', borderRadius: 8, transition: 'color .2s,background .2s', width: '100%', textAlign: 'left' as const }}>
      {label}
    </button>
  )
}
export function Nav() {
  const y = useScrollY()
  const w = useWindowWidth()
  const isMobile = w < 768
  const stuck = y > 20
  const [menuOpen, setMenuOpen] = useState(false)
  const links = [
    { label: 'Problem', id: 'problem' }, { label: 'System', id: 'system' },
    { label: 'How It Works', id: 'how' }, { label: 'Updates', id: 'updates' },
    { label: 'Pricing', id: 'pricing' }, { label: 'About', id: 'founder' },
    { label: 'Collabs', id: 'collabs' },
  ]
  return (
    <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, height: isMobile ? 60 : 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `0 ${isMobile ? '20px' : '52px'}`, background: stuck ? 'rgba(0,0,0,0.88)' : 'transparent', backdropFilter: stuck ? 'blur(24px) saturate(160%)' : 'none', borderBottom: stuck ? '1px solid rgba(255,255,255,0.07)' : '1px solid transparent', transition: 'background .5s,border-color .5s,backdrop-filter .5s' }}>
      <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} data-hover style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
        <img src={LOGO_SRC} alt="FretPractice" style={{ height: isMobile ? 40 : 48, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 0 10px rgba(37,89,244,0.5))' }} />
      </button>
      {!isMobile && (
        <>
          <ul style={{ display: 'flex', alignItems: 'center', gap: 4, listStyle: 'none', position: 'absolute', left: '50%', transform: 'translateX(-50%)', margin: 0, padding: 0 }}>
            {links.map(l => <li key={l.id}><NavBtn label={l.label} id={l.id} /></li>)}
          </ul>
          <a href="https://play.google.com/store/apps/details?id=com.fretpractice.app" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#2559F4', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, padding: '9px 20px', borderRadius: 10, textDecoration: 'none', transition: 'box-shadow .35s cubic-bezier(.16,1,.3,1),transform .3s cubic-bezier(.16,1,.3,1)', whiteSpace: 'nowrap' as const }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, flexShrink: 0 }}><path d="M3.18 23.75c.34.19.72.25 1.1.19L14.84 12 11.4 8.56 3.18 23.75z" fill="#EA4335" /><path d="M20.64 10.35l-2.94-1.67-3.68 3.32 3.68 3.67 2.97-1.69c.85-.48.85-1.63-.03-2.63z" fill="#FBBC04" /><path d="M4.28.25a1.47 1.47 0 0 0-.5.44l10.62 10.63 3.44-3.44L4.28.25z" fill="#34A853" /><path d="M3.68.69a1.47 1.47 0 0 0-.5 1.16v20.3c0 .45.18.86.5 1.16l.1.09L14.84 12v-.27L3.78.6l-.1.09z" fill="#4285F4" /></svg>
            Download Now
          </a>
        </>
      )}
      {isMobile && (
        <button onClick={() => setMenuOpen(!menuOpen)} data-hover style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ width: 20, height: 2, background: '#fff', borderRadius: 1, transform: menuOpen ? 'rotate(45deg) translate(5px,5px)' : 'none', transition: 'transform .3s' }} />
          <span style={{ width: 20, height: 2, background: '#fff', borderRadius: 1, opacity: menuOpen ? 0 : 1, transition: 'opacity .3s' }} />
          <span style={{ width: 20, height: 2, background: '#fff', borderRadius: 1, transform: menuOpen ? 'rotate(-45deg) translate(7px,-6px)' : 'none', transition: 'transform .3s' }} />
        </button>
      )}
      {isMobile && menuOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'rgba(0,0,0,0.96)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.1)', padding: '20px' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {links.map(l => <li key={l.id}><NavBtn label={l.label} id={l.id} onClick={() => setMenuOpen(false)} /></li>)}
          </ul>
          <a href="https://play.google.com/store/apps/details?id=com.fretpractice.app" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#2559F4', color: '#fff', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, padding: '14px 30px', borderRadius: 10, textDecoration: 'none', width: '100%' }}>
            <svg viewBox="0 0 24 24" style={{ width: 15, height: 15, flexShrink: 0 }}><path d="M3.18 23.75c.34.19.72.25 1.1.19L14.84 12 11.4 8.56 3.18 23.75z" fill="#EA4335" /><path d="M20.64 10.35l-2.94-1.67-3.68 3.32 3.68 3.67 2.97-1.69c.85-.48.85-1.63-.03-2.63z" fill="#FBBC04" /><path d="M4.28.25a1.47 1.47 0 0 0-.5.44l10.62 10.63 3.44-3.44L4.28.25z" fill="#34A853" /><path d="M3.68.69a1.47 1.47 0 0 0-.5 1.16v20.3c0 .45.18.86.5 1.16l.1.09L14.84 12v-.27L3.78.6l-.1.09z" fill="#4285F4" /></svg>
            Download Now
          </a>
        </div>
      )}
    </nav>
  )
}

// ─── WAVEFORM ────────────────────────────────────────────────────────
function WaveformBars({ count = 20 }: { count?: number }) {
  const d = useRef(Array.from({ length: count }, () => 0.6 + Math.random() * 0.9))
  const dl = useRef(Array.from({ length: count }, (_, i) => i * 0.04))
  const h = useRef(Array.from({ length: count }, () => `${20 + Math.random() * 70}%`))
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 52 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ flex: 1, borderRadius: 3, background: 'linear-gradient(180deg,#2559F4 0%,rgba(37,89,244,0.25) 100%)', height: h.current[i], animation: `wvPump ${d.current[i]}s ease-in-out infinite`, animationDelay: `${dl.current[i]}s` }} />
      ))}
    </div>
  )
}

// ─── MOCK CARD ───────────────────────────────────────────────────────
function MockCard({ tag, title, children }: { tag: string; title: string; children: React.ReactNode }) {
  const [hov, setHov] = useState(false)
  return (
    <div data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${hov ? 'rgba(37,89,244,0.35)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 12, padding: 18, position: 'relative', overflow: 'hidden', transition: 'border-color .35s' }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(37,89,244,0.45),transparent)', opacity: hov ? 1 : 0, transition: 'opacity .35s' }} />
      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 9, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2559F4', marginBottom: 6 }}>{tag}</div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 13, letterSpacing: '0.04em', color: '#fff', marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  )
}

// ─── APP MOCKUP ──────────────────────────────────────────────────────
function AppMockup() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const fills = useRef<(HTMLDivElement | null)[]>([])
  const prog: Array<[string, number]> = [['Scales', 82], ['Chords', 65], ['Timing', 74]]
  const fret = [[0, 0, 1, 0, 0, 1, 0, 0], [0, 1, 0, 0, 1, 0, 0, 0], [1, 0, 0, 1, 0, 0, 0, 1], [0, 0, 1, 0, 0, 0, 1, 0], [0, 1, 0, 0, 0, 1, 0, 0], [1, 0, 0, 0, 1, 0, 0, 0]]
  useEffect(() => { const t = setTimeout(() => { fills.current.forEach((el, i) => { if (el) el.style.width = `${prog[i][1]}%` }) }, 1200); return () => clearTimeout(t) }, [])
  return (
    <div style={{ marginTop: isMobile ? 60 : 84, width: '100%', maxWidth: isMobile ? '100%' : 920, position: 'relative', animation: 'riseIn 1s cubic-bezier(.16,1,.3,1) .7s both' }}>
      <div style={{ position: 'absolute', bottom: -50, left: '50%', transform: 'translateX(-50%)', width: '60%', height: 80, background: 'radial-gradient(ellipse,rgba(37,89,244,0.32) 0%,transparent 70%)', filter: 'blur(20px)', pointerEvents: 'none' }} />
      <div style={{ background: '#0f1320', border: '1px solid rgba(255,255,255,0.08)', borderRadius: isMobile ? 16 : 20, overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.03),0 4px 16px rgba(0,0,0,0.4),0 24px 80px rgba(0,0,0,0.55)' }}>
        <div style={{ background: 'rgba(0,0,0,0.55)', padding: isMobile ? '10px 14px' : '13px 18px', display: 'flex', alignItems: 'center', gap: 7, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {['#FF5F57', '#FEBC2E', '#28C840'].map(c => <span key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c, display: 'block' }} />)}
          <div style={{ margin: '0 auto', background: 'rgba(255,255,255,0.06)', borderRadius: 5, padding: isMobile ? '3px 12px' : '4px 18px', fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 9 : 11, color: 'rgba(255,255,255,0.25)' }}>{isMobile ? 'fretpractice.com' : 'fretpractice.com — Practice Session'}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: isMobile ? 12 : 14, padding: isMobile ? 16 : 24 }}>
          <MockCard tag="Fretboard" title={isMobile ? "G Major" : "FretBoard Visualization "}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {fret.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 2, height: 9 }}>
                  {row.map((v, ci) => <div key={ci} style={{ flex: 1, borderRadius: 2, background: v ? '#2559F4' : 'rgba(255,255,255,0.05)', boxShadow: v ? '0 0 7px rgba(37,89,244,0.65)' : 'none', animation: v ? 'fbPulse 3s ease-in-out infinite' : 'none' }} />)}
                </div>
              ))}
            </div>
          </MockCard>
          <MockCard tag="Live Audio" title="Real-time Feedback">
            <WaveformBars count={isMobile ? 12 : 18} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px rgba(34,197,94,0.55)', flexShrink: 0 }} />
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 9 : 10.5, color: '#22c55e' }}>{isMobile ? 'G4 detected' : 'G4 detected — Accurate'}</span>
            </div>
          </MockCard>
          <MockCard tag="Progress" title="This Week">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {prog.map(([lbl, pct], i) => (
                <div key={lbl}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 8.5 : 9.5, color: 'rgba(255,255,255,0.28)', marginBottom: 4 }}><span>{lbl}</span><span>{pct}%</span></div>
                  <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden' }}>
                    <div ref={el => { fills.current[i] = el }} style={{ height: '100%', width: 0, background: 'linear-gradient(90deg,#2559F4,#7eb8ff)', borderRadius: 10, transition: 'width 1.6s cubic-bezier(.16,1,.3,1)' }} />
                  </div>
                </div>
              ))}
            </div>
          </MockCard>
        </div>
      </div>
    </div>
  )
}

// ─── HERO ────────────────────────────────────────────────────────────
function Hero() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const [googleHov, setGoogleHov] = useState(false)
  const [awsHov, setAwsHov] = useState(false)

  return (
    <section id="hero" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '100px 20px 60px' : '110px 52px 80px', position: 'relative', overflow: 'hidden', textAlign: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', width: isMobile ? 600 : 900, height: isMobile ? 600 : 900, top: isMobile ? -200 : -300, left: isMobile ? -150 : -250, borderRadius: '50%', background: 'radial-gradient(circle,rgba(37,89,244,0.2) 0%,transparent 65%)', filter: 'blur(100px)', animation: 'floatA 20s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', width: isMobile ? 400 : 600, height: isMobile ? 400 : 600, bottom: isMobile ? -100 : -200, right: isMobile ? -50 : -100, borderRadius: '50%', background: 'radial-gradient(circle,rgba(20,33,74,0.7) 0%,transparent 65%)', filter: 'blur(100px)', animation: 'floatB 25s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle,rgba(255,255,255,0.075) 1px,transparent 1px)', backgroundSize: isMobile ? '30px 30px' : '44px 44px', maskImage: 'radial-gradient(ellipse 80% 70% at 50% 42%,black 15%,transparent 72%)', WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 42%,black 15%,transparent 72%)' }} />
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(37,89,244,0.4)', background: 'rgba(37,89,244,0.08)', borderRadius: 100, padding: isMobile ? '6px 14px 6px 10px' : '7px 16px 7px 11px', marginBottom: isMobile ? 30 : 44, animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .1s both' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2559F4', boxShadow: '0 0 0 4px rgba(37,89,244,0.2)', display: 'block', animation: 'breathePulse 2.4s ease-in-out infinite' }} />
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 10 : 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>AI powered music practice and production platform</span>
      </div>
      <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(40px,9vw,64px)' : 'clamp(52px,9vw,120px)', fontWeight: 400, lineHeight: 0.92, letterSpacing: '0.04em', color: '#fff', maxWidth: isMobile ? '100%' : 900, margin: '0 auto', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .2s both' }}>
        Practice with<br /><span style={{ background: 'linear-gradient(130deg,#7eb8ff 0%,#2559F4 45%,#1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>INTENTION.</span>
      </h1>
      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 'clamp(14px,3.5vw,16px)' : 'clamp(15px,1.4vw,18px)', color: 'rgba(255,255,255,0.48)', fontWeight: 300, lineHeight: 1.78, maxWidth: isMobile ? '100%' : 520, margin: isMobile ? '20px auto 40px' : '28px auto 40px', animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .3s both' }}>
        Structured sessions, real-time feedback, AI tools — one system built to move you forward, not just keep you busy.
      </p>

      {/* Accelerated by trust logos */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        margin: '0 auto 36px',
        animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .45s both'
      }}>
        <span style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 11,
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          color: 'rgba(255,255,255,0.24)'
        }}>
          Accelerated by
        </span>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          gap: isMobile ? 18 : 32,
        }}>
          {/* Google for Startups */}
          <div
            onMouseEnter={() => setGoogleHov(true)}
            onMouseLeave={() => setGoogleHov(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              opacity: googleHov ? 1 : 0.45,
              transform: googleHov ? 'scale(1.02)' : 'none',
              transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
              cursor: 'pointer'
            }}
            onClick={() => window.open('https://startup.google/', '_blank')}
          >
            <svg viewBox="0 0 24 24" style={{ width: 17, height: 17, flexShrink: 0 }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
            </svg>
            <span style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              color: '#fff',
              letterSpacing: '-0.01em'
            }}>
              Google for Startups <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 300 }}>Cloud Program</span>
            </span>
          </div>

          {/* Divider dot on desktop */}
          {!isMobile && <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />}

          {/* AWS Activate */}
          <div
            onMouseEnter={() => setAwsHov(true)}
            onMouseLeave={() => setAwsHov(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              opacity: awsHov ? 1 : 0.45,
              transform: awsHov ? 'scale(1.02)' : 'none',
              transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
              cursor: 'pointer'
            }}
            onClick={() => window.open('https://aws.amazon.com/activate/', '_blank')}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1, position: 'relative', top: -1 }}>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' }}>aws</span>
              <svg viewBox="0 0 20 6" style={{ width: 20, height: 6, fill: '#ff9900', marginTop: -2 }}>
                <path d="M18.7 4.4c-2.4 1.8-5.8 2.8-9.1 2.8-4.7 0-8.9-2-11.6-5.2-.3-.3-.1-.8.3-.6 4.9 2.5 10.9 3.9 16.5 1.7.5-.2.8.3.3.7z" />
                <path d="M19.9 1.8c-.4-.6-1.7-.4-2.4-.2-.2.1-.2-.1 0-.2.9-.6 2.3-.4 2.7.2.4.6-.2 1.9-.9 2.5-.2.2-.3 0-.2-.2.5-.7.8-1.5.8-2.1z" />
              </svg>
            </div>
            <span style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              fontWeight: 500,
              color: '#fff',
              letterSpacing: '-0.01em'
            }}>
              AWS Activate <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 300 }}>Startup Program</span>
            </span>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, animation: 'riseIn .9s cubic-bezier(.16,1,.3,1) .6s both' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'rgba(255,255,255,0.25)', fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' as const }}>
          <span style={{ display: 'block', width: 20, height: 1, background: 'rgba(255,255,255,0.18)' }} />
          Available now on
          <span style={{ display: 'block', width: 20, height: 1, background: 'rgba(255,255,255,0.18)' }} />
        </div>
        <DownloadButtons size={isMobile ? 'sm' : 'md'} />
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: isMobile ? 10.5 : 11.5,
          color: 'rgba(255,255,255,0.22)',
          letterSpacing: '0.04em',
          marginTop: 6,
          marginBottom: 0
        }}>
          Early access for serious guitarists. Limited spots.
        </p>
      </div>
      <AppMockup />
    </section>
  )
}

// ─── NUMBERS STRIP ───────────────────────────────────────────────────
const STATS: StatItem[] = [
  { val: 'Early', lbl: 'Development Stage' },
  { val: 'AI-First', lbl: 'Platform Architecture' },
  { val: '200M+', lbl: "Users via founder's past work" },
  { val: '18 yrs', lbl: "Founder's guitar experience" },
]
function StatCell({ val, lbl, last }: { val: string; lbl: string; last: boolean }) {
  const [hov, setHov] = useState(false)
  const w = useWindowWidth()
  const isMobile = w < 768
  return (
    <div data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ padding: isMobile ? '22px 16px' : '32px 44px', borderRight: last ? 'none' : '1px solid rgba(255,255,255,0.07)', background: hov ? 'rgba(37,89,244,0.04)' : 'transparent', transition: 'background .3s', cursor: 'pointer', textAlign: 'center' }}>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(22px,5vw,32px)' : 'clamp(28px,3.5vw,42px)', letterSpacing: '0.04em', color: '#fff', marginBottom: 5 }}>{val}</div>
      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 10 : 12, color: 'rgba(255,255,255,0.28)', lineHeight: 1.3 }}>{lbl}</div>
    </div>
  )
}
function NumbersStrip() {
  const w = useWindowWidth()
  const isMobile = w < 768
  return (
    <Reveal>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${isMobile ? 2 : 4},1fr)`, borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {STATS.map((s, i) => <StatCell key={i} val={s.val} lbl={s.lbl} last={isMobile ? (i === 1 || i === 3) : (i === 3)} />)}
      </div>
    </Reveal>
  )
}
// ─── PROBLEM SECTION ─────────────────────────────────────────────────
function ProblemSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const sRef = useRef<HTMLDivElement>(null)
  const cardTx = useCallback((_: number) => {
    return 'none'
  }, [])
  const cards = [
    { label: 'Session Focus', val: '94', unit: '%', badge: '↑ 31% this month', extra: null, icon: '◎' },
    { label: 'Practice Streak', val: '12', unit: ' days', badge: null, extra: 'streak', icon: '▦' },
    { label: 'Avg Improvement', val: '+28', unit: '% accuracy', badge: null, extra: null, icon: '↗' },
  ]
  return (
    <section id="problem" style={{ padding: isMobile ? '80px 0' : '140px 0', background: '#0a0d17' }}>
      <div ref={sRef} style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 40 : 100, alignItems: 'center' }}>
        <Reveal>
          <Eyebrow>The Problem</Eyebrow>
          <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(28px,7vw,48px)' : 'clamp(36px,5vw,68px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', marginBottom: isMobile ? 20 : 36 }}>
            Most guitarists<br />practice.<br /><span style={{ color: '#2559F4' }}>Few improve.</span>
          </h2>
          {["You have the chords. You have the tabs. You even have the time. But progress feels random — like you're spinning in place.", "The real issue isn't effort. It's structure. Without a system, practice is just playing — and playing isn't progress.", 'FretPractice changes that. Every session has a goal, every note has feedback, every week has measurable data.'].map((p, i) => (
            <p key={i} style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 13 : 15.5, lineHeight: 1.85, color: 'rgba(255,255,255,0.46)', fontWeight: 300, marginBottom: 14 }}>{p}</p>
          ))}
        </Reveal>
        <Reveal delay={0.18}>
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {cards.map((c, i) => (
                <div key={i} data-hover style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#2559F4', flexShrink: 0 }}>{c.icon}</div>
                  <div>
                    <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.28)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</div>
                    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 24, letterSpacing: '0.02em', color: '#fff', lineHeight: 1 }}>{c.val}<span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 300, color: 'rgba(255,255,255,0.3)' }}>{c.unit}</span></div>
                    {c.badge && <div style={{ marginTop: 6, display: 'inline-flex', background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: 9, fontWeight: 500, padding: '2px 8px', borderRadius: 100, fontFamily: "'DM Sans',sans-serif" }}>{c.badge}</div>}
                    {c.extra === 'streak' && <div style={{ marginTop: 8, display: 'flex', gap: 3 }}>{[1, 1, 1, 0.35, 0.08, 0.08, 0.08].map((op, j) => <div key={j} style={{ width: 12, height: 12, borderRadius: 3, background: `rgba(37,89,244,${op})` }} />)}</div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ position: 'relative', height: 400 }}>
              {cards.map((c, i) => (
                <div key={i} data-hover style={{ position: 'absolute', top: i === 0 ? 0 : i === 1 ? 65 : 'auto', left: i === 0 ? 0 : i === 2 ? 45 : 'auto', right: i === 1 ? 0 : 'auto', bottom: i === 2 ? 10 : 'auto', width: i === 0 ? 215 : i === 1 ? 230 : 210, background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 24, boxShadow: '0 24px 70px rgba(0,0,0,0.55)', transform: cardTx(i), transition: 'transform .8s cubic-bezier(.16,1,.3,1)', animation: `scFloat ${9 + i * 3}s ease-in-out infinite ${i * 2}s`, cursor: 'pointer' }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, fontSize: 14, color: '#2559F4' }}>{c.icon}</div>
                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10.5, color: 'rgba(255,255,255,0.28)', marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: '0.02em', color: '#fff', lineHeight: 1 }}>{c.val}<span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.3)' }}>{c.unit}</span></div>
                  {c.badge && <div style={{ marginTop: 10, display: 'inline-flex', background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: 10, fontWeight: 500, padding: '3px 9px', borderRadius: 100, fontFamily: "'DM Sans',sans-serif" }}>{c.badge}</div>}
                  {c.extra === 'streak' && <div style={{ marginTop: 10, display: 'flex', gap: 3 }}>{[1, 1, 1, 0.35, 0.08, 0.08, 0.08].map((op, j) => <div key={j} style={{ width: 14, height: 14, borderRadius: 3, background: `rgba(37,89,244,${op})` }} />)}</div>}
                </div>
              ))}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  )
}

// ─── SYSTEM SECTION ──────────────────────────────────────────────────
const FEATURES: Feature[] = [
  {
    num: '01',
    frontTitle: 'Ear Training',
    frontTagline: 'Train your relative pitch and interval recognition.',
    backTitle: 'Ear Training Practice',
    backDesc: 'Develop the ability to play what you hear by recognizing intervals, chords, and progressions. FretPractice uses interactive listening exercises linked to fretboard paths, helping you build an intuitive bridge between sound and position.',
    icon: 'ear'
  },
  {
    num: '02',
    frontTitle: 'CAGED System',
    frontTagline: 'Master neck navigation using open chord shapes.',
    backTitle: 'CAGED Fretboard Map',
    backDesc: 'Bridge the gap between raw notes and scale patterns. FretPractice visualizes root positions and interval relationships across all 5 CAGED blocks in real-time, letting you transpose ideas to any key instantly.',
    icon: 'caged'
  },
  {
    num: '03',
    frontTitle: 'Scale Practice',
    frontTagline: 'Build finger fluidness and pattern recall.',
    backTitle: 'Scale Practice Generator',
    backDesc: 'Practice pentatonic patterns, major modes, and exotic scales with dynamic virtual neck overlays. A built-in tempo ladder ramps up the speed as you play cleanly, converting muscle memory into fast, accurate soloing.',
    icon: 'scale'
  },
  {
    num: '04',
    frontTitle: 'Arpeggio Practice',
    frontTagline: 'Target chord tones cleanly across the neck.',
    backTitle: 'Arpeggio Practice Engine',
    backDesc: 'Learn to highlight key changes in your leads rather than just running scales. FretPractice maps out major, minor, and seventh arpeggio paths, showing you clear shapes for sweep picking and interval skips.',
    icon: 'arpeggio'
  },
  {
    num: '05',
    frontTitle: 'Chord Library',
    frontTagline: 'Master open, barre, and jazz voice variations.',
    backTitle: 'Interactive Chord Engine',
    backDesc: 'Stop searching for chord charts online. FretPractice includes a comprehensive database of open positions, movable barre shapes, CAGED variants, and jazz voicings, all mapped out visually with sound previews.',
    icon: 'chord'
  },
  {
    num: '06',
    frontTitle: 'Audio Analyzer',
    frontTagline: 'Real-time pitch detection and accuracy feedback.',
    backTitle: 'Precision Pitch Engine',
    backDesc: 'Plug in or use your device microphone to get instant notes. Our polyphonic analyzer checks your execution, showing you exactly which notes you hit, when you were late, and how cleanly you executed the line.',
    icon: 'analyzer'
  }
]

function renderFeatureIcon(type: string, color: string) {
  switch (type) {
    case 'ear':
      return (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round' }}>
          <line x1="4" y1="12" x2="4" y2="12" />
          <line x1="8" y1="8" x2="8" y2="16" />
          <line x1="12" y1="4" x2="12" y2="20" />
          <line x1="16" y1="6" x2="16" y2="18" />
          <line x1="20" y1="10" x2="20" y2="14" />
        </svg>
      )
    case 'caged':
      return (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="9" y1="3" x2="9" y2="21" />
          <line x1="15" y1="3" x2="15" y2="21" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="3" y1="15" x2="21" y2="15" />
          <circle cx="9" cy="9" r="2" fill={color} />
          <circle cx="15" cy="15" r="2" fill={color} />
        </svg>
      )
    case 'scale':
      return (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round' }}>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
          <circle cx="6" cy="18" r="2" fill={color} />
          <circle cx="12" cy="12" r="2" fill={color} />
          <circle cx="18" cy="6" r="2" fill={color} />
        </svg>
      )
    case 'arpeggio':
      return (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: 'none', stroke: color, strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' }}>
          <polygon points="12,3 21,20 3,20" />
          <circle cx="12" cy="3" r="2" fill={color} />
          <circle cx="7" cy="13" r="2" fill={color} />
          <circle cx="17" cy="13" r="2" fill={color} />
        </svg>
      )
    case 'chord':
      return (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
          <line x1="6" y1="4" x2="6" y2="20" />
          <line x1="10" y1="4" x2="10" y2="20" />
          <line x1="14" y1="4" x2="14" y2="20" />
          <line x1="18" y1="4" x2="18" y2="20" />
          <line x1="6" y1="4" x2="18" y2="4" strokeWidth="2.5" />
          <line x1="6" y1="9" x2="18" y2="9" />
          <line x1="6" y1="14" x2="18" y2="14" />
          <line x1="6" y1="19" x2="18" y2="19" />
          <circle cx="10" cy="11.5" r="1.5" fill={color} />
          <circle cx="14" cy="16.5" r="1.5" fill={color} />
        </svg>
      )
    case 'analyzer':
      return (
        <svg viewBox="0 0 24 24" style={{ width: 18, height: 18, fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
          <circle cx="12" cy="12" r="9" />
          <path d="M6 12 Q9 6, 12 12 T18 12" />
        </svg>
      )
    default:
      return null
  }
}

function FeatureCard({ f }: { f: Feature }) {
  const [flipped, setFlipped] = useState(false)
  const isMobile = useWindowWidth() < 768

  return (
    <div
      style={{
        perspective: '1000px',
        width: '100%',
        height: isMobile ? 220 : 250,
        cursor: 'pointer',
      }}
      onMouseEnter={() => !isMobile && setFlipped(true)}
      onMouseLeave={() => !isMobile && setFlipped(false)}
      onClick={() => isMobile && setFlipped(!flipped)}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front Face */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            background: '#0a0d17',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '16px',
            padding: '24px 20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
          }}
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.15em', color: '#2559F4', opacity: 0.7 }}>{f.num}</div>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2559F4' }}>
                {renderFeatureIcon(f.icon, '#2559F4')}
              </div>
            </div>
            <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 21, letterSpacing: '0.04em', color: '#fff', marginBottom: 8, lineHeight: 1.1 }}>{f.frontTitle}</h3>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, lineHeight: 1.5, color: 'rgba(255,255,255,0.44)', fontWeight: 300, margin: 0 }}>{f.frontTagline}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#2559F4', fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>
            <span>View details</span>
            <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: 'currentColor' }}><path d="M10 6L8.59 7.41 13.17 12l-4.59 4.59L10 18l6-6z" /></svg>
          </div>
        </div>

        {/* Back Face */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            background: '#0d1223',
            border: '1px solid rgba(37,89,244,0.35)',
            borderRadius: '16px',
            padding: '24px 20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
          }}
        >
          <div>
            <h4 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, letterSpacing: '0.04em', color: '#7eb8ff', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.1 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>{renderFeatureIcon(f.icon, '#7eb8ff')}</span> {f.backTitle}
            </h4>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, lineHeight: 1.5, color: 'rgba(255,255,255,0.5)', fontWeight: 300, margin: 0 }}>{f.backDesc}</p>
          </div>

        </div>
      </div>
    </div>
  )
}

function SystemSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const isTablet = w >= 768 && w < 1024
  const cols = isMobile ? 1 : isTablet ? 2 : 3
  return (
    <section id="system" style={{ padding: isMobile ? '80px 0' : '140px 0', background: '#000' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>
        <Reveal>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 64, gap: 40, flexWrap: 'wrap' }}>
            <div>
              <Eyebrow>The System</Eyebrow>
              <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(40px,5.5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff' }}>
                Multiple tools.<br />One purpose.
              </h2>
            </div>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15.5, lineHeight: 1.75, color: 'rgba(255,255,255,0.44)', fontWeight: 300, maxWidth: 480 }}>
              We built FretPractice because we were tired of switching between five different apps just to practice. Now featuring 8+ integrated tools — including our real-time tuner, smart metronome, ear trainer, CAGED map, scale and arpeggio generators, interactive chord library, audio analyzer, and DHUN AI engine — everything you need is organized in one seamless workspace.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols},1fr)`,
            gap: isMobile ? 16 : 24,
            overflow: 'visible',
          }}>
            {FEATURES.map((f, i) => <FeatureCard key={i} f={f} />)}
          </div>
        </Reveal>
      </div>
    </section>
  )
}


function FeatureMap() {
  const w = useWindowWidth()
  const isMobile = w < 768

  // cx, cy = center of node circle; lx, ly = label anchor point; la = text-anchor
  const CX = 550, CY = 440  // hub center

  const node = (cx: number, cy: number, lx: number, ly: number, la: 'start' | 'end' | 'middle', label: string[]) =>
    ({ cx, cy, lx, ly, la, label })

  const nodes = [
    node(CX, 115, CX, 80, 'middle', ['Create Tabs from Audio']),
    node(CX - 230, 210, CX - 270, 210, 'end', ['Tempo Ladder Mode']),
    node(CX + 230, 210, CX + 270, 210, 'start', ['Intelligent Reminders']),
    node(CX - 310, 320, CX - 350, 320, 'end', ['Riff & Lick Vault']),
    node(CX + 320, 350, CX + 360, 350, 'start', ['Dhun Mode']),
    node(CX - 360, 440, CX - 400, 440, 'end', ['Metronome+']),
    node(CX + 360, 440, CX + 400, 440, 'start', ['Add/Import Tabs']),
    node(CX - 310, 555, CX - 350, 555, 'end', ['Progress Snapshots']),
    node(CX + 280, 540, CX + 320, 540, 'start', ['Instructor Feedback']),
    node(CX - 200, 665, CX - 240, 665, 'end', ['Jam Assist']),
    node(CX + 185, 665, CX + 225, 665, 'start', ['Custom Practice Routine']),
    node(CX, 765, CX, 810, 'middle', ['Smart Practice Paths']),
  ]

  // Routing: path from hub edge to node circle edge (right-angle L-shapes)
  const lineColor = 'rgba(255,255,255,0.28)'
  const lw = '0.8'
  const paths = [
    // Top: straight vertical
    `M${CX} ${CY - 70} L${CX} ${nodes[0].cy + 28}`,
    // Top-left: down then left
    `M${CX - 45} ${CY - 55} L${CX - 45} ${nodes[1].cy} L${nodes[1].cx + 28} ${nodes[1].cy}`,
    // Top-right: down then right
    `M${CX + 45} ${CY - 55} L${CX + 45} ${nodes[2].cy} L${nodes[2].cx - 28} ${nodes[2].cy}`,
    // Left-upper: left then up
    `M${CX - 70} ${CY - 90} L${CX - 70} ${nodes[3].cy} L${nodes[3].cx + 28} ${nodes[3].cy}`,
    // Right-upper: right then up
    `M${CX + 70} ${CY - 60} L${CX + 70} ${nodes[4].cy} L${nodes[4].cx - 28} ${nodes[4].cy}`,
    // Left: straight horizontal
    `M${CX - 70} ${CY} L${nodes[5].cx + 28} ${CY}`,
    // Right: straight horizontal
    `M${CX + 70} ${CY} L${nodes[6].cx - 28} ${CY}`,
    // Left-lower
    `M${CX - 70} ${CY + 90} L${CX - 70} ${nodes[7].cy} L${nodes[7].cx + 28} ${nodes[7].cy}`,
    // Right-lower
    `M${CX + 70} ${CY + 60} L${CX + 70} ${nodes[8].cy} L${nodes[8].cx - 28} ${nodes[8].cy}`,
    // Bottom-left
    `M${CX - 45} ${CY + 70} L${CX - 45} ${nodes[9].cy} L${nodes[9].cx + 28} ${nodes[9].cy}`,
    // Bottom-right
    `M${CX + 45} ${CY + 70} L${CX + 45} ${nodes[10].cy} L${nodes[10].cx - 28} ${nodes[10].cy}`,
    // Bottom: straight vertical
    `M${CX} ${CY + 70} L${CX} ${nodes[11].cy - 28}`,
  ]

  return (
    <section style={{ padding: isMobile ? '60px 0' : '80px 0', background: 'radial-gradient(ellipse 70% 60% at 50% 50%, #0c1a22 0%, #060d12 42%, #020508 70%, #000 100%)' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: `0 ${isMobile ? '16px' : '40px'}` }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 28 : 48 }}>
            <Eyebrow center>The Full System</Eyebrow>
            <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(36px,5vw,64px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff' }}>
              Everything connected.<br />All in one place.
            </h2>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div style={{ width: '100%', margin: '0 auto', overflowX: isMobile ? 'auto' : 'visible' }}>
            <svg viewBox="0 0 1100 900" width="100%" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
              <defs>
                <radialGradient id="fmGlow2" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#1e8ab0" stopOpacity="0.45" />
                  <stop offset="50%" stopColor="#0a2e40" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="#000" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* Ambient center glow */}
              <ellipse cx={CX} cy={CY} rx="280" ry="260" fill="url(#fmGlow2)" />

              {/* ── CONNECTOR LINES ── */}
              {paths.map((d, i) => (
                <path key={i} d={d} fill="none" stroke={lineColor} strokeWidth={lw} />
              ))}

              {/* ── HUB CIRCLE ── */}
              {/* outer glow ring */}
              <circle cx={CX} cy={CY} r="78" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="12" />
              {/* main circle */}
              <circle cx={CX} cy={CY} r="70" fill="#04090f" stroke="#ffffff" strokeWidth="3" />
              {/* actual logo image */}
              <image href="/images/logo 1.png" x={CX - 42} y={CY - 42} width="84" height="84" />

              {/* ── FEATURE NODES ── */}
              {nodes.map(({ cx, cy, lx, ly, la, label }, i) => (
                <g key={i}>
                  {/* node circle */}
                  <circle cx={cx} cy={cy} r="28" fill="#060c14" stroke="rgba(255,255,255,0.5)" strokeWidth="0.9" />
                  {/* icon placeholder — 16×16 icon area, rendered per-node below */}
                  {/* labels */}
                  {label.map((line, j) => (
                    <text
                      key={j}
                      x={lx}
                      y={ly + j * 17 - (label.length - 1) * 8.5}
                      textAnchor={la}
                      fontFamily="'DM Sans',Inter,sans-serif"
                      fontSize="13.5"
                      fontWeight="400"
                      fill="rgba(255,255,255,0.82)"
                    >{line}</text>
                  ))}
                </g>
              ))}

              {/* ── ICONS inside each node ── */}

              {/* 0: Create Tabs from Audio — mic */}
              <g><rect x={CX - 5} y={103} width="10" height="16" rx="5" fill="none" stroke="#fff" strokeWidth="1.2" />
                <path d={`M${CX - 8} 119 Q${CX - 8} 127 ${CX} 127 Q${CX + 8} 127 ${CX + 8} 119`} fill="none" stroke="#fff" strokeWidth="1.2" />
                <line x1={CX} y1="127" x2={CX} y2="131" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                <line x1={CX - 4} y1="131" x2={CX + 4} y2="131" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" /></g>

              {/* 1: Tempo Ladder Mode — grid/ladder */}
              {(() => {
                const { cx, cy } = nodes[1]; return (
                  <g><line x1={cx - 10} y1={cy - 8} x2={cx + 10} y2={cy - 8} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                    <line x1={cx - 10} y1={cy} x2={cx + 10} y2={cy} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                    <line x1={cx - 10} y1={cy + 8} x2={cx + 10} y2={cy + 8} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                    <line x1={cx - 10} y1={cy - 8} x2={cx - 10} y2={cy + 8} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                  </g>);
              })()}

              {/* 2: Intelligent Reminders — clock */}
              {(() => {
                const { cx, cy } = nodes[2]; return (
                  <g><circle cx={cx} cy={cy} r="13" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <line x1={cx} y1={cy - 10} x2={cx} y2={cy} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                    <line x1={cx} y1={cy} x2={cx + 7} y2={cy + 4} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                  </g>);
              })()}

              {/* 3: Riff & Lick Vault — vinyl record */}
              {(() => {
                const { cx, cy } = nodes[3]; return (
                  <g><circle cx={cx} cy={cy} r="13" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <circle cx={cx} cy={cy} r="5" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <circle cx={cx} cy={cy} r="2" fill="#fff" />
                    <path d={`M${cx} ${cy - 13} A13 13 0 0 1 ${cx + 11} ${cy + 7}`} fill="none" stroke="#060c14" strokeWidth="3" />
                    <path d={`M${cx - 5} ${cy - 5} A7 7 0 0 1 ${cx + 5} ${cy + 5}`} fill="none" stroke="#060c14" strokeWidth="2" />
                  </g>);
              })()}

              {/* 4: Dhun Mode — music note */}
              {(() => {
                const { cx, cy } = nodes[4]; return (
                  <g><path d={`M${cx - 6} ${cy - 10} L${cx - 6} ${cy + 6} M${cx - 6} ${cy - 10} L${cx + 8} ${cy - 14} L${cx + 8} ${cy + 2}`} fill="none" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    <ellipse cx={cx - 8} cy={cy + 7} rx="4" ry="3" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <ellipse cx={cx + 6} cy={cy + 3} rx="4" ry="3" fill="none" stroke="#fff" strokeWidth="1.2" />
                  </g>);
              })()}

              {/* 5: Metronome+ — triangle pendulum */}
              {(() => {
                const { cx, cy } = nodes[5]; return (
                  <g><path d={`M${cx - 9} ${cy + 11} L${cx} ${cy - 12} L${cx + 9} ${cy + 11} Z`} fill="none" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
                    <line x1={cx} y1={cy + 3} x2={cx + 7} y2={cy - 7} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                  </g>);
              })()}

              {/* 6: Add/Import Tabs — document */}
              {(() => {
                const { cx, cy } = nodes[6]; return (
                  <g><path d={`M${cx - 9} ${cy - 12} L${cx - 9} ${cy + 13} L${cx + 9} ${cy + 13} L${cx + 9} ${cy - 6} L${cx + 3} ${cy - 12} Z`} fill="none" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
                    <path d={`M${cx + 3} ${cy - 12} L${cx + 3} ${cy - 6} L${cx + 9} ${cy - 6}`} fill="none" stroke="#fff" strokeWidth="1.2" />
                    <line x1={cx - 6} y1={cy} x2={cx + 6} y2={cy} stroke="#fff" strokeWidth="1" strokeLinecap="round" />
                    <line x1={cx - 6} y1={cy + 6} x2={cx + 6} y2={cy + 6} stroke="#fff" strokeWidth="1" strokeLinecap="round" />
                  </g>);
              })()}

              {/* 7: Progress Snapshots — camera */}
              {(() => {
                const { cx, cy } = nodes[7]; return (
                  <g><rect x={cx - 11} y={cy - 7} width="22" height="16" rx="2" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <circle cx={cx} cy={cy + 1} r="5" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <path d={`M${cx - 5} ${cy - 7} L${cx - 3} ${cy - 12} L${cx + 3} ${cy - 12} L${cx + 5} ${cy - 7}`} fill="none" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round" />
                  </g>);
              })()}

              {/* 8: Instructor Feedback — two people */}
              {(() => {
                const { cx, cy } = nodes[8]; return (
                  <g><circle cx={cx - 6} cy={cy - 8} r="4" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <path d={`M${cx - 14} ${cy + 8} Q${cx - 14} ${cy + 1} ${cx - 6} ${cy + 1} Q${cx + 2} ${cy + 1} ${cx + 2} ${cy + 8}`} fill="none" stroke="#fff" strokeWidth="1.2" />
                    <circle cx={cx + 7} cy={cy - 8} r="4" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <path d={`M${cx - 1} ${cy + 8} Q${cx - 1} ${cy + 1} ${cx + 7} ${cy + 1} Q${cx + 15} ${cy + 1} ${cx + 15} ${cy + 8}`} fill="none" stroke="#fff" strokeWidth="1.2" />
                  </g>);
              })()}

              {/* 9: Jam Assist — sunburst */}
              {(() => {
                const { cx, cy } = nodes[9]; return (
                  <g><circle cx={cx} cy={cy} r="7" fill="none" stroke="#fff" strokeWidth="1.2" />
                    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
                      const r = Math.PI * deg / 180;
                      return <line key={i} x1={cx + 9 * Math.cos(r)} y1={cy + 9 * Math.sin(r)} x2={cx + 13 * Math.cos(r)} y2={cy + 13 * Math.sin(r)} stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                    })}
                  </g>);
              })()}

              {/* 10: Custom Practice Routine — checklist */}
              {(() => {
                const { cx, cy } = nodes[10]; return (
                  <g><rect x={cx - 10} y={cy - 12} width="20" height="24" rx="2" fill="none" stroke="#fff" strokeWidth="1.2" />
                    <line x1={cx - 7} y1={cy - 5} x2={cx + 7} y2={cy - 5} stroke="#fff" strokeWidth="1" strokeLinecap="round" />
                    <line x1={cx - 7} y1={cy + 1} x2={cx + 7} y2={cy + 1} stroke="#fff" strokeWidth="1" strokeLinecap="round" />
                    <line x1={cx - 7} y1={cy + 7} x2={cx + 1} y2={cy + 7} stroke="#fff" strokeWidth="1" strokeLinecap="round" />
                  </g>);
              })()}

              {/* 11: Smart Practice Paths — route/infinity loop */}
              {(() => {
                const { cx, cy } = nodes[11]; return (
                  <g><path d={`M${cx - 12} ${cy} C${cx - 12} ${cy - 12} ${cx} ${cy - 12} ${cx} ${cy} C${cx} ${cy + 12} ${cx + 12} ${cy + 12} ${cx + 12} ${cy} C${cx + 12} ${cy - 12} ${cx} ${cy - 12} ${cx} ${cy}`} fill="none" stroke="#fff" strokeWidth="1.2" />
                  </g>);
              })()}

            </svg>
          </div>
        </Reveal>
      </div>
    </section>
  )
}




// ─── DHUN AI SECTION ─────────────────────────────────────────────────
function DhunSection() {
  const w = useWindowWidth()
  const isMobile = w < 768

  return (
    <section id="dhun" style={{ padding: isMobile ? '80px 0' : '130px 0', background: '#000', position: 'relative', overflow: 'hidden' }}>
      {/* Ambient glows */}
      <div style={{ position: 'absolute', top: '20%', left: '-10%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,89,244,0.12) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '10%', right: '-5%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(126,184,255,0.07) 0%, transparent 70%)', filter: 'blur(80px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>

        {/* Header */}
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 64 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(37,89,244,0.35)', background: 'rgba(37,89,244,0.08)', borderRadius: 100, padding: '6px 16px 6px 10px', marginBottom: 24 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2559F4', boxShadow: '0 0 0 3px rgba(37,89,244,0.2)', display: 'block', animation: 'breathePulse 2.4s ease-in-out infinite' }} />
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: 'rgba(255,255,255,0.55)' }}>Smart Backing Track</span>
            </div>
            <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(36px,9vw,56px)' : 'clamp(48px,6vw,80px)', fontWeight: 400, lineHeight: 0.95, letterSpacing: '0.04em', color: '#fff', marginBottom: 20 }}>
              Meet{' '}
              <span style={{ background: 'linear-gradient(130deg,#7eb8ff 0%,#2559F4 45%,#1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Dhun AI</span>
            </h2>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 17, color: 'rgba(255,255,255,0.45)', fontWeight: 300, lineHeight: 1.75, maxWidth: 520, margin: '0 auto 28px' }}>
              Your intelligent backing track generator. Pick a key, set your tempo — Dhun creates real musical context so you practice with intention, not in silence.
            </p>
            {/* Feature pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              {['Any key & scale', 'Custom tempo', 'Genre-aware', 'Loop-ready', 'AI-generated'].map(tag => (
                <span key={tag} style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.5)', background: 'rgba(37,89,244,0.08)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 100, padding: '5px 14px', letterSpacing: '0.04em' }}>{tag}</span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Video embed */}
        <Reveal delay={0.15}>
          <div style={{ position: 'relative', borderRadius: isMobile ? 16 : 22, overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.07), 0 32px 100px rgba(0,0,0,0.7), 0 0 60px rgba(37,89,244,0.15)' }}>
            {/* Gradient border top accent */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #2559F4, #7eb8ff, #2559F4, transparent)', zIndex: 2, pointerEvents: 'none' }} />
            <div style={{ padding: '56.25% 0 0 0', position: 'relative', background: '#050810' }}>
              <iframe
                src="https://player.vimeo.com/video/1189822033?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1&loop=1"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Meet Dhun AI – The Future of Backing Track Gen | FretPractice"
              />
            </div>
          </div>
        </Reveal>

        {/* Bottom caption */}
        <Reveal delay={0.25}>
          <p style={{ textAlign: 'center', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.22)', marginTop: 24, letterSpacing: '0.03em' }}>
            Dhun AI is part of the FretPractice system — Coming Soon.
          </p>
        </Reveal>

      </div>
    </section>
  )
}

// ─── STOP SWITCHING BANNER ────────────────────────────────────────────
function StopSwitchingBanner() {
  const items = [
    { text: 'Stop switching apps while practising', serif: true },
    { text: 'FretPractice brings everything you need — in one place', serif: false },
    { text: 'Metronome. Fretboard. Audio feedback. AI tracks. Progress.', serif: true },
    { text: 'All here. All connected.', serif: false },
  ]
  const rep = [...items, ...items, ...items, ...items]
  return (
    <div style={{ background: '#2559F4', overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.15)', borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
      <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', padding: '22px 0', animation: 'marqueeScroll 30s linear infinite' }}>
        {rep.map((item, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 20, padding: '0 44px' }}>
            <span style={{ fontFamily: item.serif ? "'Bebas Neue',sans-serif" : "'DM Sans',sans-serif", fontSize: item.serif ? 'clamp(15px,1.7vw,20px)' : 16, fontWeight: item.serif ? 400 : 300, letterSpacing: item.serif ? '0.05em' : '0.01em', color: item.serif ? '#fff' : 'rgba(255,255,255,0.65)', fontStyle: item.serif ? 'normal' : 'italic' }}>{item.text}</span>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
          </span>
        ))}
      </div>
    </div>
  )
}

// ─── HOW IT WORKS ────────────────────────────────────────────────────
const STEPS: StepItem[] = [
  { n: '1', title: 'Set your goal', desc: 'Tell FretPractice exactly what you want to achieve — a scale, a technique, a speed milestone. The system builds your plan.' },
  { n: '2', title: 'Practice with guided sessions', desc: 'Every session is structured. Real-time audio tells you when you nail it — and when to slow down and reset.' },
  { n: '3', title: 'Track progress and improve', desc: 'Watch your data move. No plateau, no guessing, no random sessions. Clear, measurable growth every week.' },
]
function StepRow({ s, last }: { s: StepItem; last: boolean }) {
  const [hov, setHov] = useState(false)
  return (
    <div data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', gap: 22, padding: '28px 0', paddingLeft: hov ? 8 : 0, borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.07)', transition: 'padding-left .3s cubic-bezier(.16,1,.3,1)', cursor: 'pointer' }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: hov ? '#2559F4' : 'rgba(37,89,244,0.1)', border: `1px solid ${hov ? '#2559F4' : 'rgba(37,89,244,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Bebas Neue',sans-serif", fontSize: 15, letterSpacing: '0.04em', color: hov ? '#fff' : '#2559F4', transition: 'all .3s cubic-bezier(.16,1,.3,1)' }}>{s.n}</div>
      <div>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: '0.04em', color: '#fff', marginBottom: 8 }}>{s.title}</div>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.75, color: 'rgba(255,255,255,0.44)', fontWeight: 300 }}>{s.desc}</div>
      </div>
    </div>
  )
}
function HowSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const timer = useTimer()
  const [vidHov, setVidHov] = useState(false)
  return (
    <section id="how" style={{ padding: isMobile ? '80px 0' : '140px 0', background: '#0a0d17' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 40 : 100, alignItems: 'center' }}>
        <Reveal>
          <Eyebrow>How It Works</Eyebrow>
          <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(40px,5.5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', marginBottom: 52 }}>Three steps.<br />Zero<br />guesswork.</h2>
          <div>{STEPS.map((s, i) => <StepRow key={i} s={s} last={i === 2} />)}</div>
        </Reveal>
        <Reveal delay={0.2}>
          <div style={{ background: '#111827', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, overflow: 'hidden', boxShadow: '0 40px 100px rgba(0,0,0,0.65)' }}>
            <div style={{ padding: '16px 22px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 15, letterSpacing: '0.04em', color: '#fff' }}>Practice Session</span>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 15, letterSpacing: '0.04em', color: '#2559F4' }}>{timer}</span>
            </div>
            <div style={{ padding: 22 }}>
              <div style={{ background: 'rgba(37,89,244,0.1)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 12, padding: '16px 18px', marginBottom: 18 }}>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 9.5, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2559F4', marginBottom: 6 }}>Current Exercise</div>
                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, letterSpacing: '0.04em', color: '#fff' }}>G Major Scale — Position 1</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.07)', marginBottom: 18 }}>
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.28)' }}>Tempo</span>
                <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 32, letterSpacing: '0.02em', color: '#fff' }}>96<span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 300, color: 'rgba(255,255,255,0.3)', marginLeft: 3 }}>BPM</span></span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 6 }}><span>Session Progress</span><span>68%</span></div>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 10, overflow: 'hidden', marginBottom: 18 }}>
                <div style={{ height: '100%', width: '68%', background: 'linear-gradient(90deg,#2559F4,#7eb8ff)', borderRadius: 10 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.18)', borderRadius: 9, padding: '11px 14px' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: '#22c55e' }}>Clean execution. Try 4 BPM faster.</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Glimpse Video Embed */}
      <div style={{ maxWidth: 1100, margin: `${isMobile ? '80px' : '120px'} auto 0`, padding: `0 ${isMobile ? '20px' : '52px'}`, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: '100%', maxWidth: 800 }}>
          <Reveal delay={0.1}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 32 : 44 }}>
              <Eyebrow center={true}>See a Glimpse</Eyebrow>
              <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(28px,4.5vw,52px)', fontWeight: 400, letterSpacing: '0.04em', color: '#fff', margin: '0 auto 14px', lineHeight: 1 }}>
                What's There!!...
              </h3>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.44)', fontWeight: 300, maxWidth: 540, margin: '0 auto', lineHeight: 1.6 }}>
                A first look at the smart visual metronome, real-time feedback system, and adaptive practice loops in action.
              </p>
            </div>
          </Reveal>
        </div>

        <div style={{ width: '100%', maxWidth: 800 }}>
          <Reveal delay={0.2}>
            <div
              onMouseEnter={() => setVidHov(true)}
              onMouseLeave={() => setVidHov(false)}
              style={{
                width: '100%',
                background: '#0a0d17',
                border: `1px solid ${vidHov ? 'rgba(37,89,244,0.4)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: vidHov ? '0 24px 72px rgba(0,0,0,0.55), 0 0 40px rgba(37,89,244,0.08)' : '0 12px 32px rgba(0,0,0,0.3)',
                transform: vidHov ? 'translateY(-4px)' : 'none',
                transition: 'all .35s cubic-bezier(.16,1,.3,1)',
                position: 'relative'
              }}
            >
              {/* Top accent line */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #2559F4, transparent)', transform: vidHov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .5s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center', zIndex: 2 }} />

              <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: '#000' }}>
                <iframe
                  src="https://www.youtube.com/embed/xujS-KxJTeA"
                  title="FretPractice — A First Glimpse of What's There!!..."
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

// ─── TWEET CARD ──────────────────────────────────────────────────────
function TweetSkeleton() {
  return (
    <div style={{ background: '#0a0d17', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 18, padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', flexShrink: 0, animation: 'skeletonPulse 1.5s ease-in-out infinite' }} />
        <div style={{ flex: 1, height: 14, borderRadius: 7, background: 'rgba(255,255,255,0.08)', animation: 'skeletonPulse 1.5s ease-in-out infinite' }} />
      </div>
      {['80%', '100%', '65%'].map((w, i) => (
        <div key={i} style={{ width: w, height: 11, borderRadius: 6, background: 'rgba(255,255,255,0.06)', animation: `skeletonPulse 1.5s ease-in-out infinite ${i * 0.15}s` }} />
      ))}
    </div>
  )
}
function TweetCard({ tweet }: { tweet: Tweet }) {
  const [hov, setHov] = useState(false)
  const [liked, setLiked] = useState(false)
  const renderText = (text: string) =>
    text.split(/(@FretPracticeNow)/g).map((part, i) =>
      part === '@FretPracticeNow'
        ? <span key={i} style={{ color: '#2559F4', fontWeight: 500 }}>@FretPracticeNow</span>
        : <span key={i}>{part}</span>
    )
  return (
    <div data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: hov ? '#0d1223' : '#0a0d17', border: `1px solid ${hov ? 'rgba(37,89,244,0.3)' : 'rgba(255,255,255,0.07)'}`, borderRadius: 18, padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: 16, position: 'relative', overflow: 'hidden', transition: 'background .35s,border-color .35s,transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s', transform: hov ? 'translateY(-4px)' : 'none', boxShadow: hov ? '0 20px 60px rgba(0,0,0,0.5)' : 'none', cursor: 'pointer' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,#2559F4,transparent)', transform: hov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .45s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={tweet.avatar} alt={tweet.author} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }} />
          <div>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500, color: '#fff', lineHeight: 1.2 }}>{tweet.author}</div>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{tweet.handle}</div>
          </div>
        </div>
        <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </div>
      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14.5, lineHeight: 1.72, color: 'rgba(255,255,255,0.72)', fontWeight: 300, margin: 0, flex: 1 }}>{renderText(tweet.text)}</p>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11.5, color: 'rgba(255,255,255,0.25)' }}>{tweet.date}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: 'rgba(255,255,255,0.25)' }}>
              <path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2H13v2H7.5c-2.209 0-4-1.79-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 6H11V4h5.5c2.209 0 4 1.79 4 4v8.45l2.068-1.93 1.364 1.46L19.5 20.12l-4.432-4.14 1.364-1.46 2.068 1.93V8c0-1.1-.896-2-2-2z" />
            </svg>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.28)' }}>{tweet.retweets}</span>
          </div>
          <button data-hover onClick={() => setLiked(l => !l)} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: liked ? '#f43f5e' : 'rgba(255,255,255,0.25)', transition: 'fill .2s' }}>
              <path d="M16.697 5.5c-1.222-.06-2.679.51-3.89 2.16l-.805 1.09-.806-1.09C9.984 6.01 8.526 5.44 7.304 5.5c-1.243.07-2.349.78-2.91 1.91-.552 1.12-.633 2.78.479 4.582 1.074 1.77 3.252 3.83 6.129 6.05 2.877-2.22 5.055-4.28 6.13-6.05 1.111-1.802 1.03-3.462.477-4.582-.561-1.13-1.666-1.84-2.912-1.91z" />
            </svg>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: liked ? '#f43f5e' : 'rgba(255,255,255,0.28)', transition: 'color .2s' }}>{liked ? tweet.likes + 1 : tweet.likes}</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── UPDATES SECTION ─────────────────────────────────────────────────
function UpdatesSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const isTablet = w >= 768 && w < 1024
  const cols = isMobile ? 1 : isTablet ? 2 : 3
  const { tweets, loading, error, lastFetched, refresh } = useTweets()
  const [expanded, setExpanded] = useState(false)
  const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  return (
    <section id="updates" style={{ padding: isMobile ? '80px 0' : '140px 0', background: '#000' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>
        <Reveal>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 64, gap: 24, flexWrap: 'wrap' }}>
            <div>
              <Eyebrow>Build in Public</Eyebrow>
              <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(40px,5.5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff' }}>Latest Updates</h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: TWITTER_CONFIG.LIVE_MODE ? 'rgba(34,197,94,0.08)' : 'rgba(37,89,244,0.08)', border: `1px solid ${TWITTER_CONFIG.LIVE_MODE ? 'rgba(34,197,94,0.2)' : 'rgba(37,89,244,0.2)'}`, borderRadius: 100, padding: '6px 14px' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: TWITTER_CONFIG.LIVE_MODE ? '#22c55e' : '#2559F4', boxShadow: TWITTER_CONFIG.LIVE_MODE ? '0 0 6px rgba(34,197,94,0.6)' : '0 0 6px rgba(37,89,244,0.6)', display: 'block', animation: 'breathePulse 2s ease-in-out infinite' }} />
                <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: TWITTER_CONFIG.LIVE_MODE ? '#22c55e' : 'rgba(255,255,255,0.6)' }}>
                  {TWITTER_CONFIG.LIVE_MODE ? 'Live feed' : 'Demo — set LIVE_MODE:true to go live'}
                </span>
              </div>
              {TWITTER_CONFIG.LIVE_MODE && (
                <button onClick={refresh} data-hover style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                  <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'currentColor' }}><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" /></svg>
                  Refresh
                </button>
              )}
              <a href="https://x.com/FretPracticeNow" target="_blank" rel="noopener noreferrer" data-hover
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.45)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', transition: 'color .2s,border-color .2s' }}
                onMouseEnter={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.color = '#fff'; el.style.borderColor = 'rgba(255,255,255,0.25)' }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLAnchorElement; el.style.color = 'rgba(255,255,255,0.45)'; el.style.borderColor = 'rgba(255,255,255,0.1)' }}>
                <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'currentColor' }}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                Follow @FretPracticeNow
              </a>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          {error && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, padding: '12px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(239,68,68,0.8)' }}>Could not reach Twitter API — showing demo tweets</span>
              <button onClick={refresh} data-hover style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 7, padding: '5px 14px', cursor: 'pointer', fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(239,68,68,0.8)' }}>Try again</button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 18 }}>
            {loading
              ? Array.from({ length: 3 }).map((_, i) => <TweetSkeleton key={i} />)
              : tweets.slice(0, 3).map(t => <TweetCard key={t.id} tweet={t} />)}
          </div>
          {!loading && tweets.length > 3 && (
            <div style={{
              maxHeight: expanded ? '2000px' : '0px',
              opacity: expanded ? 1 : 0,
              overflow: 'hidden',
              transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
              marginTop: expanded ? 18 : 0,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 18 }}>
                {tweets.slice(3).map(t => <TweetCard key={t.id} tweet={t} />)}
              </div>
            </div>
          )}
          {!loading && tweets.length > 3 && (
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <button
                onClick={() => setExpanded(!expanded)}
                data-hover
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 10,
                  padding: '12px 28px',
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = 'rgba(37,89,244,0.5)';
                  e.currentTarget.style.boxShadow = '0 0 15px rgba(37,89,244,0.2)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <span>{expanded ? 'Show Less' : 'Show More Updates'}</span>
                <svg
                  viewBox="0 0 24 24"
                  style={{
                    width: 14,
                    height: 14,
                    fill: 'currentColor',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                >
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                </svg>
              </button>
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: TWITTER_CONFIG.LIVE_MODE ? '#22c55e' : '#2559F4', display: 'block', animation: 'breathePulse 2.4s ease-in-out infinite' }} />
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
              {TWITTER_CONFIG.LIVE_MODE
                ? lastFetched ? `Last updated ${fmt(lastFetched)} · Auto-refreshes every 5 minutes` : 'Loading...'
                : 'Demo mode · Set LIVE_MODE: true in TWITTER_CONFIG after deploying api/tweets.ts'}
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── FOR WHO ─────────────────────────────────────────────────────────
function WhoItem({ text, last }: { text: string; last: boolean }) {
  const [hov, setHov] = useState(false)
  return (
    <div data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '20px 0', paddingLeft: hov ? 4 : 0, borderBottom: last ? 'none' : '1px solid rgba(255,255,255,0.07)', fontFamily: "'DM Sans',sans-serif", fontSize: 15.5, color: hov ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.46)', lineHeight: 1.6, transition: 'color .25s,padding-left .3s cubic-bezier(.16,1,.3,1)', cursor: 'pointer' }}>
      <span style={{ color: '#2559F4', flexShrink: 0, marginTop: 3, opacity: hov ? 1 : 0.6, transform: hov ? 'translateX(5px)' : 'none', transition: 'opacity .25s,transform .3s cubic-bezier(.16,1,.3,1)', fontSize: 14 }}>→</span>
      {text}
    </div>
  )
}
function WhoSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const items = ['Just getting serious and need a real system to follow', 'Stuck at intermediate despite hours of practice', 'Trying to break out of a plateau with clarity and structure', 'Want to understand the fretboard — not just memorize tabs', 'Serious about tracking and measuring their own growth']
  return (
    <section id="who" style={{ padding: isMobile ? '80px 0' : '140px 0', background: '#0a0d17' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 40 : 80, alignItems: 'start' }}>
        <Reveal>
          <Eyebrow>Built For</Eyebrow>
          <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(36px,5vw,64px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', marginBottom: 44 }}>Guitarists who want<br />to improve.<br />Not just play.</h2>
          <div>{items.map((item, i) => <WhoItem key={i} text={item} last={i === 4} />)}</div>
        </Reveal>
        <Reveal delay={0.2}>
          <div style={{ background: 'linear-gradient(150deg,#111827 0%,#0a0d17 100%)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 22, padding: isMobile ? 32 : 48, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: -16, left: 28, fontFamily: "'Bebas Neue',sans-serif", fontSize: 160, letterSpacing: '0.02em', color: 'rgba(37,89,244,0.08)', lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>"</div>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 15 : 18, lineHeight: 1.72, color: 'rgba(255,255,255,0.72)', fontStyle: 'italic', fontWeight: 300, marginBottom: 30, position: 'relative' }}>Practicing without structure is like training without a coach. You put in the hours but you can't explain why you're not improving. FretPractice changes that.</p>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 16, letterSpacing: '0.04em', color: '#fff', marginBottom: 4 }}>Rivu Chakraborty</div>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.28)' }}>Founder · Guitarist · 18+ years · Student of Amyt Dutta</div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── PRICING ─────────────────────────────────────────────────────────
const PRICING_FEATURES: PricingFeature[] = [
  { name: 'Metronome & Tuner', free: '✓', premium: '✓', ultra: '✓' },
  { name: 'Practice Routine', free: '✓', premium: '✓', ultra: '✓' },
  { name: 'Chord Library', free: 'Basic', premium: 'Advanced', ultra: 'Advanced' },
  { name: 'Chord Analyser', free: 'Basic', premium: 'Advanced', ultra: 'Advanced' },
  { name: 'Progress Tracking', free: '✓', premium: '✓', ultra: '✓' },
  { name: 'AI Progress Feedback', free: '—', premium: '—', ultra: '✓' },
  { name: 'Dhun AI Backing Tracks', free: '—', premium: 'Limited', ultra: 'Unlimited' },
  { name: 'Ear Training', free: '✓', premium: '✓', ultra: '✓' },
  { name: 'CAGED Practice', free: 'Limited', premium: 'Limited', ultra: 'Unlimited' },
  { name: 'Scale Practice', free: '—', premium: '—', ultra: '✓' },
  { name: 'Arpeggio Practice', free: '—', premium: '—', ultra: '✓' },
]

const PRICING_TIERS: PricingTierData[] = [
  { tier: 'Free', priceINR: '₹0', priceUSD: '$0', note: 'Core tools, always free', cta: 'Get Started' },
  { tier: 'Premium', priceINR: '₹99', priceUSD: '$4.99', note: 'Everything you need to grow', badge: 'Best Value', hot: true, cta: 'Get Premium' },
  { tier: 'Ultra', priceINR: '₹299', priceUSD: '$9.99', note: 'For the most serious players', cta: 'Go Ultra' },
]

const ULTRA_COMING_SOON: string[] = []

function FeatureStatus({ status, isMobileSm }: { status: string; isMobileSm?: boolean }) {
  const size = isMobileSm ? 18 : 20
  const iconSize = isMobileSm ? 10 : 12

  if (status === '✓' || status === 'Advanced' || status === 'Unlimited' || status === 'Active' || status === 'Limited' || status === 'Basic') {
    return (
      <span style={{ width: size, height: size, borderRadius: '50%', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg viewBox="0 0 24 24" style={{ width: iconSize, height: iconSize, fill: '#22c55e' }}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
      </span>
    )
  }
  // Default to '—' or unavailable
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ width: 8, height: 1.5, background: 'rgba(255,255,255,0.2)', borderRadius: 1, display: 'block' }} />
    </span>
  )
}

function PriceCard({ t, tierIndex, currency, features, onCta }: { t: PricingTierData; tierIndex: number; currency: 'INR' | 'USD'; features: PricingFeature[]; onCta: () => void }) {
  const [hov, setHov] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const w = useWindowWidth()
  const isMobileSm = w < 380
  const isMobile = w < 640
  const isTablet = w >= 640 && w < 1024
  const price = currency === 'INR' ? t.priceINR : t.priceUSD
  const tierKey = tierIndex === 0 ? 'free' : tierIndex === 1 ? 'premium' : 'ultra'
  const cardPad = isMobileSm ? 22 : isMobile ? 26 : isTablet ? 28 : 38

  const available = features.filter(f => f[tierKey as keyof PricingFeature] !== '—')
  const unavailable = features.filter(f => f[tierKey as keyof PricingFeature] === '—')
  const sortedFeatures = [...available, ...unavailable]

  return (
    <div data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: t.hot ? 'linear-gradient(155deg,#101d3d 0%,#111827 100%)' : '#111827',
        border: `1px solid ${t.hot ? 'rgba(37,89,244,0.4)' : hov ? 'rgba(37,89,244,0.22)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: isMobileSm ? 18 : 22,
        padding: cardPad,
        position: 'relative',
        overflow: 'hidden',
        transform: t.hot ? (hov ? 'translateY(-8px) scale(1.02)' : 'translateY(-4px)') : (hov ? 'translateY(-5px)' : 'none'),
        boxShadow: t.hot
          ? (hov ? '0 36px 90px rgba(0,0,0,0.55), 0 0 60px rgba(37,89,244,0.12)' : '0 24px 60px rgba(0,0,0,0.4), 0 0 40px rgba(37,89,244,0.06)')
          : (hov ? '0 30px 80px rgba(0,0,0,0.45)' : 'none'),
        transition: 'transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s, border-color .35s',
        cursor: 'pointer',
        zIndex: t.hot ? 2 : 1,
      }}>
      {/* Top gradient accent for hot tier */}
      {t.hot && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg,transparent,#2559F4,#88b4ff,transparent)' }} />}

      {/* Badge */}
      {t.badge && (
        <div style={{ position: 'absolute', top: isMobileSm ? 14 : 18, right: isMobileSm ? 14 : 18, background: 'linear-gradient(135deg, #2559F4, #7eb8ff)', color: '#fff', fontSize: isMobileSm ? 8.5 : 9.5, fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', padding: isMobileSm ? '3px 10px' : '4px 12px', borderRadius: 100, fontFamily: "'DM Sans',sans-serif", boxShadow: '0 4px 16px rgba(37,89,244,0.3)' }}>
          {t.badge}
        </div>
      )}

      {/* Tier name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isMobileSm ? 10 : 14 }}>
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 10 : 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#2559F4' }}>{t.tier}</span>
        {t.tier === 'Ultra' && (
          <span style={{
            fontFamily: "'DM Sans',sans-serif",
            fontSize: isMobileSm ? 8 : 9,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase' as const,
            color: '#7eb8ff',
            background: 'rgba(37,89,244,0.08)',
            border: '1px solid rgba(37,89,244,0.25)',
            padding: '2px 8px',
            borderRadius: 100,
            whiteSpace: 'nowrap',
            boxShadow: '0 0 10px rgba(37,89,244,0.05)'
          }}>
            Coming Soon
          </span>
        )}
      </div>

      {/* Price */}
      <div style={{ marginBottom: 4 }}>
        <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobileSm ? 36 : isMobile ? 40 : isTablet ? 42 : 48, letterSpacing: '0.02em', color: '#fff', lineHeight: 1 }}>{price}</span>
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 12 : 15, fontWeight: 300, color: 'rgba(255,255,255,0.28)', marginLeft: 4 }}>/month</span>
      </div>

      {/* Note */}
      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 11 : 12, color: 'rgba(255,255,255,0.28)', marginBottom: isMobileSm ? 22 : 28 }}>{t.note}</div>

      {/* Feature list */}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: isMobileSm ? 10 : 12, marginBottom: isMobileSm ? 28 : 34 }}>
        {sortedFeatures.map((f, i) => {
          const status = f[tierKey as keyof PricingFeature] as string
          const isAvailable = status !== '—'
          const isTextStatus = status !== '✓' && status !== '—'
          return (
            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: isMobileSm ? 8 : 11, fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 12 : isTablet ? 12.5 : 13.5, color: isAvailable ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.22)', fontWeight: 300 }}>
              <FeatureStatus status={status} isMobileSm={isMobileSm} />
              <span style={{ flex: 1, minWidth: 0 }}>{f.name}</span>
              {isTextStatus && (
                <span style={{
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: isMobileSm ? 9 : 10,
                  fontWeight: 500,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase' as const,
                  color: status === 'Advanced' || status === 'Unlimited' || status === 'Active'
                    ? '#7eb8ff'
                    : 'rgba(255,255,255,0.35)',
                  background: status === 'Advanced' || status === 'Unlimited' || status === 'Active'
                    ? 'rgba(37,89,244,0.1)'
                    : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${status === 'Advanced' || status === 'Unlimited' || status === 'Active'
                    ? 'rgba(37,89,244,0.2)'
                    : 'rgba(255,255,255,0.08)'
                    }`,
                  borderRadius: 100,
                  padding: '2px 8px',
                  flexShrink: 0,
                  whiteSpace: 'nowrap'
                }}>
                  {status}
                </span>
              )}
            </li>
          )
        })}
      </ul>

      {/* Ultra coming soon extras — collapsible */}
      {tierIndex === 2 && ULTRA_COMING_SOON.length > 0 && (
        <div style={{ marginBottom: 0 }}>
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded) }}
            style={{ width: '100%', background: 'none', border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)', padding: '14px 0 0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: expanded ? 16 : (isMobileSm ? 28 : 34) }}
          >
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 10 : 11, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#2559F4' }}>
              {expanded ? 'Hide extras' : '+ More coming soon'}
            </span>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: '#2559F4', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .3s' }}><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" /></svg>
          </button>
          {expanded && (
            <div style={{ animation: 'riseIn .4s ease both', marginBottom: isMobileSm ? 28 : 34 }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: isMobileSm ? 8 : 10 }}>
                {ULTRA_COMING_SOON.map((f, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: isMobileSm ? 8 : 11, fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 11.5 : 12.5, color: 'rgba(255,255,255,0.3)', fontWeight: 300, fontStyle: 'italic' }}>
                    <span style={{ width: isMobileSm ? 18 : 20, height: isMobileSm ? 18 : 20, borderRadius: '50%', background: 'rgba(37,89,244,0.06)', border: '1px dashed rgba(37,89,244,0.2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg viewBox="0 0 24 24" style={{ width: 10, height: 10, fill: 'rgba(37,89,244,0.35)' }}><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* CTA */}
      {t.hot ? <BtnBlue onClick={onCta} fullWidth>{t.cta}</BtnBlue> : <BtnOutline onClick={onCta} fullWidth>{t.cta}</BtnOutline>}
    </div>
  )
}

function PricingSection({ onOpenModal }: { onOpenModal: () => void }) {
  const w = useWindowWidth()
  const isMobileSm = w < 380
  const isMobile = w < 640
  const isTablet = w >= 640 && w < 1024
  const [currency, setCurrency] = useState<'INR' | 'USD'>('INR')
  const [toggleHov, setToggleHov] = useState(false)

  // Grid: 1 col on mobile, 2 cols on small tablets (Premium spans if odd), 3 cols on desktop
  const gridCols = isMobile ? 1 : isTablet ? 2 : 3

  return (
    <section id="pricing" style={{ padding: isMobileSm ? '60px 0' : isMobile ? '70px 0' : isTablet ? '100px 0' : '140px 0', background: '#0a0d17' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobileSm ? '16px' : isMobile ? '20px' : isTablet ? '32px' : '52px'}` }}>
        <Reveal>
          <div style={{ textAlign: 'center', maxWidth: 560, margin: `0 auto ${isMobileSm ? '32px' : isMobile ? '36px' : '48px'}` }}>
            <Eyebrow center>Pricing</Eyebrow>
            <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobileSm ? 'clamp(32px,9vw,44px)' : 'clamp(40px,5.5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', marginBottom: isMobileSm ? 14 : 18 }}>Simple pricing.<br /><span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Real value.</span></h2>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 13.5 : isMobile ? 14.5 : 16, lineHeight: 1.8, color: 'rgba(255,255,255,0.44)', fontWeight: 300 }}>Tiered access for every level of commitment. Start free, upgrade when you're ready.</p>
          </div>
        </Reveal>

        {/* Currency Toggle */}
        <Reveal delay={0.05}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isMobileSm ? 24 : isMobile ? 28 : isTablet ? 36 : 48 }}>
            <div
              data-hover
              onMouseEnter={() => setToggleHov(true)}
              onMouseLeave={() => setToggleHov(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${toggleHov ? 'rgba(37,89,244,0.3)' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: 12,
                padding: 4,
                transition: 'border-color .3s',
              }}
            >
              {(['INR', 'USD'] as const).map(c => (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  style={{
                    fontFamily: "'DM Sans',sans-serif",
                    fontSize: isMobileSm ? 12 : 13,
                    fontWeight: currency === c ? 600 : 400,
                    color: currency === c ? '#fff' : 'rgba(255,255,255,0.4)',
                    background: currency === c ? '#2559F4' : 'transparent',
                    border: 'none',
                    borderRadius: 9,
                    padding: isMobileSm ? '7px 16px' : '8px 20px',
                    cursor: 'pointer',
                    transition: 'all .25s cubic-bezier(.16,1,.3,1)',
                    minWidth: isMobileSm ? 60 : 72,
                  }}
                >
                  {c === 'INR' ? '₹ INR' : '$ USD'}
                </button>
              ))}
            </div>
          </div>
        </Reveal>


        <Reveal delay={0.1}>
          {/* On tablet (2-col), show Free & Ultra side by side, then Premium full-width centered below */}
          {isTablet ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 16 }}>
                <PriceCard t={PRICING_TIERS[0]} tierIndex={0} currency={currency} features={PRICING_FEATURES} onCta={onOpenModal} />
                <PriceCard t={PRICING_TIERS[2]} tierIndex={2} currency={currency} features={PRICING_FEATURES} onCta={onOpenModal} />
              </div>
              <div style={{ maxWidth: 440, margin: '0 auto' }}>
                <PriceCard t={PRICING_TIERS[1]} tierIndex={1} currency={currency} features={PRICING_FEATURES} onCta={onOpenModal} />
              </div>
            </>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: isMobileSm ? 14 : isMobile ? 16 : 20, alignItems: 'start' }}>
              {PRICING_TIERS.map((t, i) => <PriceCard key={i} t={t} tierIndex={i} currency={currency} features={PRICING_FEATURES} onCta={onOpenModal} />)}
            </div>
          )}
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobileSm ? 11 : 12.5, color: 'rgba(255,255,255,0.22)', textAlign: 'center', marginTop: isMobileSm ? 28 : 40, letterSpacing: '0.03em' }}>Early access members get exclusive founding pricing. Prices may change at public launch.</p>
        </Reveal>
      </div>
    </section>
  )
}

// ─── FOUNDER ─────────────────────────────────────────────────────────
function ChipTag({ children }: { children: React.ReactNode }) {
  const [hov, setHov] = useState(false)
  return (
    <span data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: hov ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.32)', background: 'rgba(255,255,255,0.03)', border: `1px solid ${hov ? 'rgba(37,89,244,0.3)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 100, padding: '5px 14px', cursor: 'pointer', transition: 'color .25s,border-color .25s' }}>
      {children}
    </span>
  )
}
function FounderSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const chips = ['Student of Amyt Dutta', 'Google Developer Expert in ANDROID', '13+ yrs Engineering', '200M+ Users', '5+ Unicorn Startups Over SEA', '18+ yrs Guitar']
  return (
    <section id="founder" style={{ padding: isMobile ? '80px 0' : '140px 0', background: '#000' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>
        <Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '350px 1fr', gap: isMobile ? 40 : 72, alignItems: 'center' }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: isMobile ? '100%' : 375, height: isMobile ? 280 : 400, borderRadius: 24, background: 'linear-gradient(145deg,#111d36,#0a1020)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 30% 30%,rgba(37,89,244,0.14),transparent 65%)' }} />
                <img src="/images/rivu.jpeg" alt="Rivu Chakraborty" draggable="false" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 1, userSelect: 'none' }} />
              </div>
              <div style={{ position: 'absolute', inset: -12, borderRadius: 32, border: '1px solid rgba(37,89,244,0.12)', pointerEvents: 'none' }} />
            </div>
            <div>
              <Eyebrow>The Builder</Eyebrow>
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 'clamp(32px,4vw,52px)', letterSpacing: '0.04em', color: '#fff', marginBottom: 6 }}>Rivu Chakraborty</div>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: '#2559F4', fontWeight: 500, marginBottom: 22 }}>Founder, FretPractice · India's First Google Developer Expert in Kotlin</div>
              <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 15.5, lineHeight: 1.82, color: 'rgba(255,255,255,0.44)', maxWidth: 560, marginBottom: 30, fontWeight: 300 }}>
                13+ years in mobile engineering. Systems used by 200M+ users. Worked with 5+ unicorn startups. Built at the exact intersection of deep engineering and a lifelong passion for music — 18 years as a guitarist, student of legendary guitarist Amyt Dutta.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{chips.map((c, i) => <ChipTag key={i}>{c}</ChipTag>)}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── TEAM SECTION ─────────────────────────────────────────────────────────────
function TeamMemberCard({ member }: { member: TeamMemberItem }) {
  const [hov, setHov] = useState(false)
  const [imgErr, setImgErr] = useState(false)
  const w = useWindowWidth()
  const isMobile = w < 768
  const initials = member.name.trim().split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
  return (
    <Reveal>
      <div
        data-hover
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{ background: hov ? '#0d1223' : '#0a0d17', border: `1px solid ${hov ? 'rgba(37,89,244,0.35)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 22, overflow: 'hidden', transition: 'background .4s, border-color .35s, transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s', transform: hov ? 'translateY(-6px)' : 'none', boxShadow: hov ? '0 24px 72px rgba(0,0,0,0.55),0 0 40px rgba(37,89,244,0.08)' : 'none', cursor: 'pointer', position: 'relative' }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #2559F4, transparent)', transform: hov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .45s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center' }} />
        {/* Photo */}
        <div style={{ position: 'relative', width: '100%', paddingBottom: '100%', overflow: 'hidden', background: 'linear-gradient(145deg,#111827,#0a0d17)' }}>
          {!imgErr ? (
            <img src={member.image} alt={member.name} draggable="false" onError={() => setImgErr(true)}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', transition: 'transform .6s cubic-bezier(.16,1,.3,1)', transform: hov ? 'scale(1.05)' : 'scale(1)', userSelect: 'none' }} />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(145deg,#111827,#0d1223)' }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: '0.04em', color: '#2559F4' }}>{initials}</div>
            </div>
          )}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(to top,rgba(10,13,23,0.96),transparent)', pointerEvents: 'none' }} />
        </div>
        {/* Card content */}
        <div style={{ padding: isMobile ? '18px 18px 20px' : '22px 24px 24px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(37,89,244,0.1)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 100, padding: '4px 12px', marginBottom: 12 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2559F4', display: 'block' }} />
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#2559F4' }}>{member.role}</span>
          </div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 20 : 24, letterSpacing: '0.04em', color: '#fff', lineHeight: 1.05, marginBottom: 16 }}>{member.name}</div>
          <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 14 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {member.twitter && (
              <a href={`https://twitter.com/${member.twitter}`} target="_blank" rel="noopener noreferrer" data-hover
                style={{ width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', textDecoration: 'none', transition: 'all .25s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(37,89,244,0.12)'; (e.currentTarget as HTMLAnchorElement).style.color = '#fff' }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.04)'; (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.4)' }}>
                <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: 'currentColor' }}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
            )}
            {member.linkedin && (
              <a href={`https://linkedin.com/in/${member.linkedin}`} target="_blank" rel="noopener noreferrer" data-hover
                style={{ width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', textDecoration: 'none', transition: 'all .25s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(37,89,244,0.12)'; (e.currentTarget as HTMLAnchorElement).style.color = '#fff' }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.04)'; (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.4)' }}>
                <svg viewBox="0 0 24 24" style={{ width: 12, height: 12, fill: 'currentColor' }}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              </a>
            )}
            {member.twitter && <span style={{ marginLeft: 'auto', fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.2)' }}>@{member.twitter}</span>}
          </div>
        </div>
      </div>
    </Reveal>
  )
}

function TeamGroupGrid({ title, members }: { title: string; members: TeamMemberItem[] }) {
  const w = useWindowWidth()
  const isMobile = w < 640
  const isTablet = w >= 640 && w < 1024
  const cols = isMobile ? 1 : isTablet ? 2 : 3
  if (!members.length) return null
  const completeCount = Math.floor(members.length / cols) * cols
  const gridMembers = members.slice(0, completeCount)
  const remainder = members.slice(completeCount)
  return (
    <div style={{ marginBottom: 72 }}>
      <Reveal>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 40 }}>
          <span style={{ display: 'block', width: 24, height: 1, background: '#2559F4' }} />
          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', textTransform: 'uppercase' as const, color: '#2559F4' }}>{title}</span>
          <span style={{ display: 'block', flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
        </div>
      </Reveal>
      {gridMembers.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: isMobile ? 16 : 22, marginBottom: remainder.length ? (isMobile ? 16 : 22) : 0 }}>
          {gridMembers.map(m => <TeamMemberCard key={m.id} member={m} />)}
        </div>
      )}
      {remainder.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: isMobile ? 16 : 22 }}>
          {remainder.map(m => (
            <div key={m.id} style={{ width: isMobile ? '100%' : isTablet ? 'calc(50% - 11px)' : 'calc(33.333% - 15px)' }}>
              <TeamMemberCard member={m} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function TeamSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  return (
    <section style={{ padding: isMobile ? '60px 0 80px' : '100px 0 120px', background: '#000' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}` }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 48 : 72 }}>
            <Eyebrow center>The Team</Eyebrow>
            <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(36px,8vw,56px)' : 'clamp(44px,5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', margin: '0 auto 18px' }}>
              People behind<br />
              <span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>the practice.</span>
            </h2>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.44)', fontWeight: 300, maxWidth: 480, margin: '0 auto' }}>
              Engineers who play. Musicians who build. Every decision shaped by people obsessed with both.
            </p>
          </div>
        </Reveal>
        <TeamGroupGrid title="Team Members" members={TEAM_MEMBERS} />
        <TeamGroupGrid title="Advisors" members={TEAM_ADVISORS} />
      </div>
    </section>
  )
}

// ─── COLLABORATIONS TEASER ──────────────────────────────────────────
function CollaborationsSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const [cardHov, setCardHov] = useState(false)
  const [btnHov, setBtnHov] = useState(false)
  return (
    <section id="collabs" style={{ padding: isMobile ? '80px 0 100px' : '120px 0 140px', background: '#0a0d17', position: 'relative', overflow: 'hidden' }}>
      {/* Ambient glow */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(37,89,244,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, position: 'relative', zIndex: 2 }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: isMobile ? 40 : 60 }}>
            <Eyebrow center>Collaborations</Eyebrow>
            <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(36px,8vw,56px)' : 'clamp(44px,5vw,72px)', fontWeight: 400, lineHeight: 0.96, letterSpacing: '0.04em', color: '#fff', margin: '0 auto 18px' }}>
              Beyond the screen.<br />
              <span style={{ background: 'linear-gradient(130deg, #7eb8ff 0%, #2559F4 45%, #1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Into the scene.</span>
            </h2>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.44)', fontWeight: 300, maxWidth: 520, margin: '0 auto' }}>
              We don't just build tools — we partner with live events, connect with legendary artists, and champion the guitar community.
            </p>
          </div>
        </Reveal>

        {/* Featured Event Card */}
        <Reveal delay={0.1}>
          <div
            data-hover
            onMouseEnter={() => setCardHov(true)}
            onMouseLeave={() => setCardHov(false)}
            style={{
              background: cardHov ? 'rgba(17,24,39,0.9)' : 'rgba(10,13,23,0.9)',
              border: `1px solid ${cardHov ? 'rgba(37,89,244,0.4)' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: 22,
              padding: isMobile ? '28px 24px' : '40px 48px',
              maxWidth: 740,
              margin: '0 auto',
              position: 'relative',
              overflow: 'hidden',
              transition: 'background .4s, border-color .35s, box-shadow .4s',
              boxShadow: cardHov ? '0 24px 72px rgba(0,0,0,0.5), 0 0 40px rgba(37,89,244,0.08)' : '0 12px 40px rgba(0,0,0,0.2)',
              cursor: 'pointer',
            }}
            onClick={() => window.location.href = '/collaborations'}
          >
            {/* Top accent */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #2559F4, transparent)', transform: cardHov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .5s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center' }} />

            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 24 : 36, alignItems: isMobile ? 'flex-start' : 'center' }}>
              {/* Event Icon */}
              <div style={{ width: isMobile ? 64 : 80, height: isMobile ? 64 : 80, borderRadius: 20, background: 'rgba(37,89,244,0.12)', border: '1px solid rgba(37,89,244,0.28)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', padding: 8 }}>
                <img src="/images/Collab_img/raft_logo.png" alt="The Raft" draggable="false" style={{ width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none' }} />
              </div>

              <div style={{ flex: 1 }}>
                {/* Date + Venue badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(37,89,244,0.1)', border: '1px solid rgba(37,89,244,0.2)', borderRadius: 100, padding: '3px 10px' }}>
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10.5, fontWeight: 500, color: '#7eb8ff' }}>26th & 27th June 2026</span>
                  </div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 100, padding: '3px 10px' }}>
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10.5, fontWeight: 400, color: 'rgba(255,255,255,0.4)' }}>The Raft, Koramangala, Bengaluru</span>
                  </div>
                </div>

                <h3 style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 24 : 30, fontWeight: 600, color: '#fff', lineHeight: 1.15, marginBottom: 10 }}>
                  WD Forte'26 By THE RAFT, Bangalore
                </h3>
                <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 13 : 14, lineHeight: 1.7, color: 'rgba(255,255,255,0.4)', fontWeight: 300, marginBottom: 16, maxWidth: 500 }}>
                  FretPractice is thrilled to partner with The Raft — one of Bengaluru's most beloved live music venues, tucked in the heart of Koramangala. Founded on a deep-rooted love for music, The Raft has built a home for artists and listeners alike, hosting everything from intimate jam sessions and open mics to full band showcases across genres. As a community partner, we share a common belief: that great music is built through consistent practice, real stages, and the right community around you. The Raft gives musicians exactly that.
                </p>

              </div>
            </div>
          </div>
        </Reveal>

        {/* CTA */}
        <Reveal delay={0.2}>
          <div style={{ textAlign: 'center', marginTop: isMobile ? 32 : 44 }}>
            <a
              href="/collaborations"
              data-hover
              onMouseEnter={() => setBtnHov(true)}
              onMouseLeave={() => setBtnHov(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#2559F4',
                color: '#fff',
                textDecoration: 'none',
                fontFamily: "'DM Sans',sans-serif",
                fontSize: 14,
                fontWeight: 500,
                padding: '14px 30px',
                borderRadius: 10,
                cursor: 'pointer',
                boxShadow: btnHov ? '0 0 0 1px rgba(37,89,244,0.7), 0 16px 48px rgba(37,89,244,0.48)' : 'none',
                transform: btnHov ? 'translateY(-2px)' : 'none',
                transition: 'box-shadow .35s cubic-bezier(.16,1,.3,1), transform .3s cubic-bezier(.16,1,.3,1)',
              }}
            >
              View All Collaborations →
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

// ─── WAITLIST MODAL ──────────────────────────────────────────────────
function WaitlistModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const w = useWindowWidth()
  const isMobile = w < 768
  const [s, setS] = useState<ModalState>({ name: '', email: '', whatsapp: '', countryCode: '+91', experience: '', featureRequest: '', willingToResearch: false, done: false, error: false })
  const upd = (k: keyof ModalState, v: any) => setS(prev => ({ ...prev, [k]: v }))
  const submit = useCallback(async () => {
    const ok = s.name.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim())
    if (ok) {
      try {
        const response = await fetch('/api/waitlist', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: s.name.trim(),
            email: s.email.trim().toLowerCase(),
            whatsapp: s.whatsapp ? s.whatsapp.trim() : null,
            countryCode: s.whatsapp ? s.countryCode : null,
            guitarExperience: s.experience || null,
            featureRequest: s.featureRequest ? s.featureRequest.trim() : null,
            participateResearch: s.willingToResearch,
            userAgent: navigator.userAgent || null,
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const result = await response.json()
        console.log('Waitlist submission successful:', result)

        // Log analytics event
        if (analytics) {
          logEvent(analytics, 'waitlist_signup', {
            method: 'website_modal',
            value: 1
          })
        }

        upd('done', true)
        upd('error', false)
        setTimeout(() => {
          onClose()
          setS({ name: '', email: '', whatsapp: '', countryCode: '+91', experience: '', featureRequest: '', willingToResearch: false, done: false, error: false })
        }, 3000)
      } catch (error) {
        console.error('Error submitting to waitlist:', error)
        upd('error', true)
        setTimeout(() => upd('error', false), 1800)
      }
    } else {
      upd('error', true)
      setTimeout(() => upd('error', false), 1800)
    }
  }, [s, onClose])
  useEffect(() => { const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }; if (open) document.addEventListener('keydown', fn); return () => document.removeEventListener('keydown', fn) }, [open, onClose])
  if (!open) return null
  const inp: React.CSSProperties = { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '14px 18px', fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 300, color: '#fff', outline: 'none', cursor: 'text', transition: 'border-color .25s' }
  const lbl: React.CSSProperties = { fontFamily: "'DM Sans',sans-serif", fontSize: 12.5, color: 'rgba(255,255,255,0.7)', marginBottom: 8, display: 'block', fontWeight: 500 }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: isMobile ? 16 : 40, animation: 'fadeIn .25s ease' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: 'linear-gradient(145deg,#111827,#0a0d17)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 24, padding: isMobile ? 28 : 48, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', position: 'relative', animation: 'slideUp .3s ease', boxShadow: '0 40px 120px rgba(0,0,0,0.6),0 0 60px rgba(37,89,244,0.1)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>×</button>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(37, 89, 244, 0.18)', border: '1px solid rgba(37, 89, 244, 0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <img src="/images/logo 1.png" alt="FretPractice" style={{ width: 32, height: 32, objectFit: 'contain' }} />
          </div>
          <h3 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 28 : 34, fontWeight: 400, lineHeight: 1.1, letterSpacing: '0.04em', color: '#fff', marginBottom: 12 }}>Join the Waitlist</h3>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, lineHeight: 1.6, color: 'rgba(255,255,255,0.55)', fontWeight: 300 }}>Be the first to know when FretPractice launches. Get exclusive early access pricing.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 24 }}>
          <div><label style={lbl}>Name *</label>
            <input type="text" value={s.name} onChange={e => upd('name', e.target.value)} placeholder="Your name" autoFocus style={{ ...inp, borderColor: s.error && !s.name ? 'rgba(239,68,68,0.55)' : 'rgba(255,255,255,0.14)' }} /></div>
          <div><label style={lbl}>Email *</label>
            <input type="email" value={s.email} onChange={e => upd('email', e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} placeholder="you@example.com" style={{ ...inp, borderColor: s.error && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim()) ? 'rgba(239,68,68,0.55)' : 'rgba(255,255,255,0.14)' }} /></div>
          <div><label style={lbl}>WhatsApp (optional)</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <select value={s.countryCode} onChange={e => upd('countryCode', e.target.value)} style={{ ...inp, width: 'auto', minWidth: 80, padding: '14px 12px' }}>
                {['+1', '+91', '+44', '+61', '+81', '+65', '+971'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input type="tel" value={s.whatsapp} onChange={e => upd('whatsapp', e.target.value.replace(/\D/g, ''))} placeholder="Number without country code" style={{ ...inp, flex: 1 }} />
            </div>
          </div>
          <div><label style={lbl}>How long have you been playing? (optional)</label>
            <select value={s.experience} onChange={e => upd('experience', e.target.value)} style={inp}>
              <option value="">Select experience level</option>
              <option value="starting">Just starting out</option>
              <option value="beginner">Beginner (0–1 year)</option>
              <option value="intermediate">Intermediate (1–5 years)</option>
              <option value="advanced">Advanced (5+ years)</option>
            </select>
          </div>
          <div><label style={lbl}>What feature do you wish existed? (optional)</label>
            <textarea value={s.featureRequest} onChange={e => upd('featureRequest', e.target.value)} placeholder="Tell us about a feature you wish existed..." rows={3} style={{ ...inp, resize: 'vertical', minHeight: 80 }} /></div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={s.willingToResearch} onChange={e => upd('willingToResearch', e.target.checked)} style={{ marginTop: 2, accentColor: '#2559F4', width: 16, height: 16 }} />
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.65)', fontWeight: 300, lineHeight: 1.5 }}>I'm willing to participate in user research sessions</span>
          </label>
        </div>
        <button onClick={submit} disabled={s.done}
          style={{ width: '100%', background: 'linear-gradient(135deg,#2559F4,#7eb8ff)', border: 'none', borderRadius: 12, padding: '16px 24px', fontFamily: "'DM Sans',sans-serif", fontSize: 16, fontWeight: 500, color: '#fff', cursor: s.done ? 'not-allowed' : 'pointer', opacity: s.done ? 0.7 : 1, marginBottom: s.done ? 16 : 0 }}>
          {s.done ? "You're on the list 🎸" : 'Join Waitlist'}
        </button>
        {s.done && <div style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: '14px 18px', textAlign: 'center' }}><p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: '#22c55e', margin: 0 }}>Thank you for joining the waitlist! We'll get back to you soon.</p></div>}
        {s.error && <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: '#ef4444', textAlign: 'center', marginTop: 10 }}>Please fill in Name and Email.</p>}
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.25)', textAlign: 'center', marginTop: 20, lineHeight: 1.5 }}>We respect your privacy. No spam, ever. Unsubscribe anytime.</p>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{transform:translateY(20px) scale(.96);opacity:0}to{transform:translateY(0) scale(1);opacity:1}}`}</style>
    </div>
  )
}

// ─── PLATFORM DOWNLOAD SECTION ──────────────────────────────────────
function PlatformCard({ platform, icon, subtitle, available, link, features, hovColor }: {
  platform: string; icon: React.ReactNode; subtitle: string; available: boolean; link?: string; features: string[]; hovColor: string;
}) {
  const [hov, setHov] = useState(false)
  return (
    <div
      data-hover
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? '#0d1223' : '#0a0d17',
        border: `1px solid ${hov ? 'rgba(37,89,244,0.4)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 20,
        padding: '32px 28px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        transition: 'background .4s, border-color .35s, transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s',
        transform: hov ? 'translateY(-6px)' : 'none',
        boxShadow: hov ? `0 24px 64px rgba(0,0,0,0.55), 0 0 40px ${hovColor}` : 'none',
        position: 'relative',
        overflow: 'hidden',
        cursor: available ? 'pointer' : 'default',
      }}
    >
      {/* Top glow accent */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${available ? '#2559F4' : 'rgba(255,255,255,0.18)'}, transparent)`, transform: hov ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .45s cubic-bezier(.16,1,.3,1)', transformOrigin: 'center' }} />

      {/* Icon */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>{icon}</div>

      {/* Platform name */}
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: '0.06em', color: '#fff', marginBottom: 6, textAlign: 'center' }}>{platform}</div>
      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 24, textAlign: 'center' }}>{subtitle}</div>

      {/* CTA button */}
      {available && link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#2559F4', color: '#fff', borderRadius: 10, padding: '12px 20px', textDecoration: 'none', fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 600, marginBottom: 24, transition: 'filter .25s', filter: hov ? 'brightness(1.15)' : 'brightness(1)' }}
        >
          Download Now
        </a>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 100, padding: '6px 18px' }}>Coming Soon</span>
        </div>
      )}

      {/* Feature list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {features.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg viewBox="0 0 24 24" fill="none" style={{ width: 15, height: 15, flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" fill={available ? 'rgba(37,89,244,0.2)' : 'rgba(255,255,255,0.07)'} stroke={available ? 'rgba(37,89,244,0.5)' : 'rgba(255,255,255,0.15)'} strokeWidth="1.5" />
              <polyline points="8 12 11 15 16 9" stroke={available ? '#7eb8ff' : 'rgba(255,255,255,0.4)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: available ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.32)', fontWeight: 300 }}>{f}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function WaitlistSection() {
  const w = useWindowWidth()
  const isMobile = w < 768
  const isTablet = w >= 768 && w < 1024

  const platforms = [
    {
      platform: 'Android',
      subtitle: 'Android 7.0 and above',
      available: true,
      link: 'https://play.google.com/store/apps/details?id=com.fretpractice.app',
      features: ['Practice anywhere', 'Track your progress', 'AI-powered feedback'],
      hovColor: 'rgba(52,168,83,0.12)',
      icon: (
        <div style={{ width: 68, height: 68, borderRadius: 18, background: 'rgba(61,220,132,0.1)', border: '1px solid rgba(61,220,132,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 24 24" style={{ width: 36, height: 36 }}>
            <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.617.617 0 0 0-.83.22l-1.88 3.24A9.822 9.822 0 0 0 12 8c-1.64 0-3.18.43-4.47 1.17L5.65 5.93a.617.617 0 0 0-.82-.22.617.617 0 0 0-.26.85L6.4 9.48A9.89 9.89 0 0 0 2 18h20a9.89 9.89 0 0 0-4.4-8.52zM7 15.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm10 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" fill="#3DDC84" />
          </svg>
        </div>
      ),
    },
    {
      platform: 'iOS',
      subtitle: 'iOS 13.0 and above',
      available: false,
      features: ['Seamless experience', 'Practice on the go', 'Sync across devices'],
      hovColor: 'rgba(255,255,255,0.05)',
      icon: (
        <div style={{ width: 68, height: 68, borderRadius: 18, background: 'linear-gradient(145deg,rgba(255,255,255,0.07),rgba(255,255,255,0.03))', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 24 24" style={{ width: 34, height: 34, fill: 'rgba(255,255,255,0.82)' }}>
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
        </div>
      ),
    },
    {
      platform: 'macOS',
      subtitle: 'macOS 11.0 and above',
      available: false,
      features: ['Big screen practice', 'Advanced analytics', 'Pro features unlocked'],
      hovColor: 'rgba(255,255,255,0.05)',
      icon: (
        <div style={{ width: 68, height: 68, borderRadius: 18, background: 'linear-gradient(145deg,rgba(255,255,255,0.07),rgba(255,255,255,0.03))', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 24 24" style={{ width: 34, height: 34, fill: 'rgba(255,255,255,0.82)' }}>
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
        </div>
      ),
    },
    {
      platform: 'Windows',
      subtitle: 'Windows 10 and above',
      available: false,
      features: ['Desktop performance', 'Offline practice', 'Total control'],
      hovColor: 'rgba(255,255,255,0.05)',
      icon: (
        <div style={{ width: 68, height: 68, borderRadius: 18, background: 'linear-gradient(145deg,rgba(0,164,239,0.1),rgba(0,164,239,0.04))', border: '1px solid rgba(0,164,239,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg viewBox="0 0 24 24" style={{ width: 34, height: 34 }}>
            <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-13.051-1.949" fill="#00A4EF" />
          </svg>
        </div>
      ),
    },
  ]

  return (
    <section id="waitlist" style={{ padding: isMobile ? '80px 0 100px' : '120px 0 140px', textAlign: 'center', position: 'relative', overflow: 'hidden', background: '#000' }}>
      {/* Ambient background */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(37,89,244,0.09) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '30%', left: '10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,168,83,0.06) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1160, margin: '0 auto', padding: `0 ${isMobile ? '20px' : '52px'}`, position: 'relative', zIndex: 2 }}>
        <Reveal>
          {/* Status pill */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid rgba(37,89,244,0.35)', background: 'rgba(37,89,244,0.08)', borderRadius: 100, padding: '6px 16px', marginBottom: 28 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#3ddc84', boxShadow: '0 0 0 3px rgba(61,220,132,0.2)', display: 'block', animation: 'breathePulse 2.4s ease-in-out infinite' }} />
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.08em' }}>FretPractice is <span style={{ color: '#3ddc84' }}>live</span> on Android</span>
          </div>

          {/* Heading */}
          <h2 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: isMobile ? 'clamp(40px,10vw,64px)' : 'clamp(52px,7vw,88px)', fontWeight: 400, lineHeight: 0.95, letterSpacing: '0.04em', color: '#fff', marginBottom: 18 }}>
            Download{' '}
            <span style={{ background: 'linear-gradient(130deg,#7eb8ff 0%,#2559F4 50%,#1438b0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FretPractice</span>
          </h2>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: isMobile ? 14 : 16, color: 'rgba(255,255,255,0.44)', fontWeight: 300, marginBottom: 8 }}>One platform. Every device. Practice smarter, anywhere.</p>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', fontWeight: 300, marginBottom: isMobile ? 40 : 60 }}>Available now on Android &mdash; iOS, macOS &amp; Windows coming soon.</p>
        </Reveal>

        {/* Platform cards grid */}
        <Reveal delay={0.1}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(4,1fr)', gap: 16, marginBottom: 52 }}>
            {platforms.map((p) => (
              <PlatformCard key={p.platform} {...p} />
            ))}
          </div>
        </Reveal>

        {/* Footer tagline */}
        <Reveal delay={0.2}>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>
            🚀 Better practice. Better progress. That's FretPractice.
          </p>

        </Reveal>
      </div>
    </section>
  )
}

// ─── FOOTER ──────────────────────────────────────────────────────────
const SOCIALS: SocialItem[] = [
  { label: 'X', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z', url: 'https://x.com/fretpracticenow' },
  { label: 'Instagram', path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z', url: 'https://www.instagram.com/fretpractice/' },
  { label: 'LinkedIn', path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z', url: 'https://linkedin.com/company/fretpractice' },
  { label: 'YouTube', path: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z', url: 'https://youtube.com/@fretpractice' },
]
function SocialBtn({ s }: { s: SocialItem }) {
  const [hov, setHov] = useState(false)
  return (
    <a href={s.url || '#'} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: hov ? 'rgba(37,89,244,0.12)' : 'rgba(255,255,255,0.04)', border: `1px solid ${hov ? 'rgba(37,89,244,0.3)' : 'rgba(255,255,255,0.08)'}`, color: hov ? '#fff' : 'rgba(255,255,255,0.38)', transform: hov ? 'translateY(-2px)' : 'none', transition: 'all .3s cubic-bezier(.16,1,.3,1)', cursor: 'pointer', textDecoration: 'none' }}>
      <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: 'currentColor' }}><path d={s.path} /></svg>
    </a>
  )
}
function FLink({ children, id, href }: { children: React.ReactNode; id?: string; href?: string }) {
  const [hov, setHov] = useState(false)
  const isHome = typeof window !== 'undefined' && window.location.pathname === '/'
  return (
    <a href={id ? (isHome ? `#${id}` : `/#${id}`) : (href || '#')} onClick={id && isHome ? (e) => { e.preventDefault(); scrollTo(id) } : undefined} data-hover onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13.5, color: hov ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.38)', textDecoration: 'none', transition: 'color .22s', cursor: 'pointer' }}>
      {children}
    </a>
  )
}
export function Footer() {
  const w = useWindowWidth()
  const isMobile = w < 768
  return (
    <footer style={{ background: '#000', borderTop: '1px solid rgba(255,255,255,0.07)', padding: isMobile ? '50px 20px 30px' : '70px 52px 42px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '280px 1fr 1fr 1fr', gap: isMobile ? 32 : 60, maxWidth: 1100, margin: '0 auto', marginBottom: 60 }}>
        <div style={{ gridColumn: isMobile ? '1/-1' : 'auto' }}>
          <img src={LOGO_SRC} alt="FretPractice" style={{ height: 74, width: 'auto', objectFit: 'contain', marginBottom: 16, filter: 'drop-shadow(0 0 7px rgba(37,89,244,0.4))' }} />
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', lineHeight: 1.65, maxWidth: 220, marginBottom: 18 }}>AI-powered guitar practice. Structured sessions. Real progress.</p>
          <div style={{ marginBottom: 18 }}>
            <DownloadButtons size="sm" direction="column" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>{SOCIALS.map(s => <SocialBtn key={s.label} s={s} />)}</div>
        </div>
        <div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 18 }}>Product</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <li><FLink id="system">Features</FLink></li>
            <li><FLink id="how">How It Works</FLink></li>
            <li><FLink id="updates">Updates</FLink></li>
            <li><FLink id="pricing">Pricing</FLink></li>
          </ul>
        </div>
        <div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 18 }}>Company</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <li><FLink href="/team">Team</FLink></li>
            <li><FLink href="/collaborations">Collaborations</FLink></li>
            <li><FLink id="founder">About</FLink></li>
            <li><FLink href="/privacy">Privacy Policy</FLink></li>
            <li><FLink href="/terms">Terms of Use</FLink></li>
          </ul>
        </div>
        <div>
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', marginBottom: 18 }}>Platform</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <li><FLink href="#">FretPractice</FLink></li>
            <li><FLink id="dhun">Dhun — AI Music</FLink></li>
            <li><FLink href="https://mobrio.studio">Mobrio Studio</FLink></li>
          </ul>
        </div>
      </div>
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1100, margin: '0 auto', flexWrap: 'wrap', gap: 14 }}>
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.22)' }}>© 2026 FretPractice · A Mobrio Studio product · Built by Team FretPractice</span>

        {/* Subtle monochrome backing icons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.22)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Accelerated by</span>

          {/* Google for Startups */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, opacity: 0.35, transition: 'opacity 0.2s', cursor: 'pointer' }} title="Google for Startups Cloud Program" onClick={() => window.open('https://startup.google/', '_blank')}>
            <svg viewBox="0 0 24 24" style={{ width: 13, height: 13, fill: 'currentColor' }}>
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
            </svg>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10.5, fontWeight: 500, color: 'rgba(255,255,255,0.7)', letterSpacing: '-0.01em' }}>Google <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 300 }}>for Startups</span></span>
          </div>

          {/* AWS Activate */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, opacity: 0.35, transition: 'opacity 0.2s', cursor: 'pointer' }} title="AWS Activate Startup Program" onClick={() => window.open('https://aws.amazon.com/activate/', '_blank')}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1, position: 'relative', top: -1 }}>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.7)', letterSpacing: '-0.03em' }}>aws</span>
              <svg viewBox="0 0 20 6" style={{ width: 14, height: 4, fill: 'rgba(255,255,255,0.7)', marginTop: -2 }}>
                <path d="M18.7 4.4c-2.4 1.8-5.8 2.8-9.1 2.8-4.7 0-8.9-2-11.6-5.2-.3-.3-.1-.8.3-.6 4.9 2.5 10.9 3.9 16.5 1.7.5-.2.8.3.3.7z" />
                <path d="M19.9 1.8c-.4-.6-1.7-.4-2.4-.2-.2.1-.2-.1 0-.2.9-.6 2.3-.4 2.7.2.4.6-.2 1.9-.9 2.5-.2.2-.3 0-.2-.2.5-.7.8-1.5.8-2.1z" />
              </svg>
            </div>
            <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10.5, fontWeight: 500, color: 'rgba(255,255,255,0.7)', letterSpacing: '-0.01em' }}>AWS <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 300 }}>Activate</span></span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          <FLink href="/privacy">Privacy</FLink>
          <FLink href="/terms">Terms</FLink>
          <FLink href="mailto:fretpractice@mobrio.studio">fretpractice@mobrio.studio</FLink>
        </div>
      </div>
    </footer>
  )
}

// ─── APP ─────────────────────────────────────────────────────────────
export default function App() {
  const [modalOpen, setModalOpen] = useState(false)

  // Analytics tracking
  useEffect(() => {
    if (analytics) {
      logEvent(analytics, "page_view", { page: "landing_fretpractice" })
    } else {
      setTimeout(() => {
        if (analytics) {
          logEvent(analytics, "page_view", { page: "landing_fretpractice" })
        }
      }, 2000)
    }
  }, [])

  // Hash scroll on mount
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.substring(1)
      setTimeout(() => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      }, 300)
    }
  }, [])

  return (
    <>
      <GlobalStyles />
      <Nav />
      <Hero />
      <NumbersStrip />
      <HRule />
      <ProblemSection />
      <HRule />
      <SystemSection />
      <FeatureMap />
      <DhunSection />
      <StopSwitchingBanner />
      <HRule />
      <HowSection />
      <HRule />
      <WhoSection />
      <HRule />
      <PricingSection onOpenModal={() => setModalOpen(true)} />
      <HRule />
      <FounderSection />
      <HRule />
      <TeamSection />
      <HRule />
      <CollaborationsSection />
      <HRule />
      <UpdatesSection />
      <HRule />
      <WaitlistSection />
      <Footer />
      <WaitlistModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}
